import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Clock, Route as RouteIcon, AlertTriangle, Zap, Sparkles, Leaf, Clock3, TrendingUp, TrendingDown, MapPin, Droplets, Waves, Info, Map as MapIcon, type LucideIcon } from "lucide-react";
import { isGoogleCarrier } from "@/utils/googleCarrier";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { ClassifiedRoute } from "@/utils/scoring";
import { requestMapHighlight, type MapHighlightPayload } from "@/utils/mapHighlight";
import { logRouteInteraction, endpointsOf } from "@/utils/routeAnalytics";
import { getRouteSource } from "@/lib/routeDiagnostics";
import { plannedCardEtaSeconds } from "@/utils/etaPolicy";
import type { PoiSegmentMatch } from "@/utils/poiSegmentMap";
import { findIconicLandmarks } from "@/utils/iconicLandmarks";
import { getTimeWindow, type TimeWindow } from "@/utils/timeAware";
import { tierForScore, isLimitedData } from "@/utils/scoreTier";
import { buildRouteReason, buildTradeoff, type PeerRoute } from "@/utils/routeReason";
import type { TravelMode } from "@/api/routing";

// ---------------------------------------------------------------------------
// A BIKE IS NOT A WALK (Bike-mode audit, Aug 27). The through-park and
// car-free-path copy below was written for walking and had no mode gate, while
// the generator that sets `parkName` deliberately runs for walking AND biking
// (generateRoutes.ts:1925) — so a cyclist routed through a park was told
// "Walks through Rockefeller State Park Preserve on car-free trails".
//
// Driving never reaches any of this: enclosingParksPromise resolves to [] for
// driving (generateRoutes.ts:2754), so `parkName` / `throughParkNames` are
// walk/bike only. Only biking diverges here — every non-biking string is the
// existing wording, unchanged, on purpose.
// ---------------------------------------------------------------------------
interface TravelWords {
  /** Verb phrase leading the through-park celebration. */
  goesThrough: string;
  /** Surface name in "through {park} on ___". */
  parkTrails: string;
  /** Surface name in "on ___, away from traffic". Spelled out separately
   *  because the walking copy says "walking trails" in one slot and
   *  "car-free walking trails" in the other, and both must stay verbatim. */
  carFreeTrails: string;
}
function travelWords(mode: TravelMode | undefined): TravelWords {
  return mode === "biking"
    ? { goesThrough: "Rides through", parkTrails: "car-free paths", carFreeTrails: "car-free paths" }
    : { goesThrough: "Walks through", parkTrails: "walking trails", carFreeTrails: "car-free walking trails" };
}

const TIME_WINDOW_LABEL: Record<TimeWindow, string> = {
  morning_commute: "Morning commute",
  school_pickup: "School pickup",
  evening_commute: "Evening commute",
  night: "Night",
  weekend_daytime: "Weekend daytime",
  off_peak: "Off-peak",
};
import {
  PROFILE_LABELS, type PleasantnessProfile,
  type DetourTolerance, TOLERANCE_LABELS,
} from "@/utils/profile";

interface Props {
  route: ClassifiedRoute;
  selected: boolean;
  onClick: () => void;
  /** Duration of the fastest route in seconds — used to show "+X min" on the others. */
  fastestDuration?: number;
  /** User's selected pleasantness profile — drives the "X% more pleasant" copy. */
  profile?: PleasantnessProfile;
  /** Pleasant score of the fastest route in the current set. The card shows
   *  this route's pleasant score as a percentage *improvement* over the
   *  fastest baseline — so the metric is always meaningful and updates when
   *  routes change (e.g. after a "Drive by..." detour). */
  fastestPleasantScore?: number;
  /** How many "Drive by..." detours the user has applied to this trip.
   *  Each detour bumps the displayed pleasantness improvement by +5%. */
  detourCount?: number;
  /** Whether this card is the "For you" recommended pick. */
  recommended?: boolean;
  /** Features of the fastest route — used to express factual deltas
   *  ("−22% time on busy roads vs Direct"). */
  fastestFeatures?: ClassifiedRoute["features"];
  /** The DIRECT route's polyline — lets the reason describe where THIS route
   *  diverges from Direct (the genuine difference), not roads it shares. */
  fastestRouteCoords?: [number, number][];
  /** When true, the "Why this route?" panel takes over the card body. */
  whyOpen?: boolean;
  /** Toggle handler for the embedded "Why this route?" panel. Only shown
   *  on the selected card. */
  onToggleWhy?: () => void;
  /** Embedded explainer rendered in place of the card body when whyOpen. */
  whyContent?: ReactNode;
  /** Open the Trip-details (turn-by-turn directions) sheet for this route.
   *  Rendered as a button ON the selected card (Morgan) instead of only in
   *  the bottom bar. */
  onOpenDetails?: () => void;
  /** Open/expand the Detours panel. Rendered as a button on the selected
   *  card. */
  onOpenDetours?: () => void;
  /** Number of applied detours, shown as a small badge on the Detours button. */
  detourBadgeCount?: number;
  /** The offered-detours panel, rendered inline on the selected card when the
   *  Detours button is expanded (Morgan: list detours on the card itself). */
  detourPanel?: React.ReactNode;
  /** 1-based position in the surfaced list. When provided, the card is
   *  titled "Option N" instead of "Fastest"/"Wander"/etc. — keeping labels
   *  honest when the underlying classification name would be misleading. */
  optionNumber?: number;
  /** Highway-percentage delta vs Google Maps' route, in percentage points
   *  (positive = more highway than Google, negative = less). When provided,
   *  a tiny "vs Google" badge renders alongside the +X min pill. */
  googleHighwayDeltaPP?: number | null;
  /** Raw highway-percentage values that produced `googleHighwayDeltaPP`.
   *  Surfaced inside the badge popover so the comparison is fully
   *  transparent ("Pleasant: 12% · Google: 24%") rather than just a delta. */
  thisHighwayPct?: number | null;
  googleHighwayPct?: number | null;
  /** When the card is the "fastest" classification AND a Google Directions
   *  result is available, we display Google's exact ETA / distance / highway%
   *  so the card serves as a true side-by-side baseline (the route Google
   *  Maps would actually give for this trip). Quietly ignored otherwise. */
  googleOverride?: { distanceM: number; durationS: number; highwayPct: number } | null;
  /** Currently-selected reference for the "+X min" pill. The card surfaces a
   *  small toggle so users can switch between Google's ETA, our fastest
   *  pick, and our pleasant pick — and `fastestDuration` is expected to be
   *  the resolved duration for this mode (parent owns the resolution). */
  baselineMode?: "google" | "fastest" | "pleasant";
  /** Available durations (seconds) for each baseline option. Entries with
   *  `undefined` are hidden from the toggle. */
  baselineOptions?: { google?: number; fastest?: number; pleasant?: number };
  /** Callback when the user picks a different baseline. */
  onBaselineModeChange?: (mode: "google" | "fastest" | "pleasant") => void;
  /** The trip's travel mode. Copy only — it picks the verb and the name for a
   *  car-free surface so a bike card never calls itself a walk. Never used for
   *  routing, scoring or selection. Absent behaves exactly as walking did. */
  travelMode?: TravelMode;
  /** Google Directions ETA for this trip's fastest path, in seconds. When
   *  provided AND this card isn't the Google baseline itself, we render a
   *  plain-words pill ("3 min longer than Google Maps", "≈ same time as
   *  Google Maps") so users instantly see how the alternative compares to
   *  what Google would have offered. */
  googleDurationS?: number | null;
  /** Google Directions distance for the same A→B, in meters. When provided
   *  alongside googleDurationS, the ETA pill also surfaces the distance
   *  delta when it's meaningful (≥0.2 mi) — critical for walking and
   *  biking, where a "+27 min" comparison without distance context reads
   *  as a pace problem when it's actually a path-length problem.
   *  Real-drive note (#72). */
  googleDistanceM?: number | null;
  /** Live-traffic ETA for this route in seconds, fetched from Mapbox. When
   *  provided AND meaningfully different from the engine's optimistic
   *  duration, we render a small "+X min traffic" pill so users see at a
   *  glance which routes are getting hammered by congestion right now. */
  trafficDurationS?: number | null;
  /** Hooks for the actionable "relax filters" tip on the SameAsFastestPanel.
   *  When the Pleasant pick collapses onto Fastest because filters excluded
   *  every alternative, the panel suggests the single most-impactful
   *  filter to loosen and offers a one-tap action to do so. */
  relaxHints?: {
    hasNoHighways?: boolean;
    detourTolerance?: DetourTolerance;
    onRemoveNoHighways?: () => void;
    onIncreaseTolerance?: () => void;
  };
  /** When true, this card is the ONLY card in the list — meaning the engine
   *  found nothing meaningfully different from the Google reference. The
   *  card should be framed honestly: "the most direct way — no calmer route
   *  on this trip" instead of being marketed as a pleasant alternative. */
  isOnlyOption?: boolean;
  /** Fastest card only: the Pleasant slot is empty because every candidate on
   *  this trip is a highway. Says so instead of inventing a third card. */
  noMiddleOption?: boolean;
  /** The OTHER routes currently on screen. Passed to the reason builder so
   *  this card's one-liner describes what's *different* about this route
   *  versus its peers — instead of describing it in isolation (which made
   *  two plain routes collapse to the same sentence). */
  peers?: PeerRoute[];
}

// Variant labels + subtitles live in the locale dictionary so EN/ES/FR
// stay in sync with the explanation strings emitted by the scoring layer.
// Visual tokens (icon, accent colors) stay here — they don't translate.
import { getRouteCopy } from "@/locales/routeExplanations";

const META: Record<"fastest" | "pleasant" | "scenic", {
  Icon: LucideIcon; label: string; sub: string; accent: string; dot: string;
}> = (() => {
  const c = getRouteCopy();
  return {
    fastest: {
      Icon: Zap,
      label: c.classification.fastest.label,
      sub: c.classification.fastest.sub,
      accent: "text-route-fastest",
      dot: "bg-route-fastest",
    },
    pleasant: {
      Icon: Sparkles,
      label: c.classification.pleasant.label,
      sub: c.classification.pleasant.sub,
      accent: "text-route-pleasant",
      dot: "bg-route-pleasant",
    },
    scenic: {
      Icon: Leaf,
      label: c.classification.scenic.label,
      sub: c.classification.scenic.sub,
      accent: "text-route-scenic",
      dot: "bg-route-scenic",
    },
  };
})();

const fmtMin = (s: number) => {
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};
import { fmtDistance } from "@/utils/units";
const fmtKm = (m: number) => fmtDistance(m);

// Above this trip length the live-traffic ETA can only be an AVERAGE — Mapbox
// samples ~10 points across the whole route, so it can't reflect block-by-block
// congestion hundreds of miles out. We label it honestly and tell the driver it
// refines en route. ~50 mi (well beyond the 20-mile in-nav refinement window).
const LONG_TRIP_AVG_TRAFFIC_M = 80000;

const isNegativeChip = (text: string) =>
  /penalized|on the motorway|on (a |the )?(highway|major road)|on busy arterials|% on highway|heavier going/i.test(text);

const TIME_AWARE_RE = /^(Good fit for|Heavier going during) /i;

/**
 * "Highway vs Google" badge with an inline ⓘ legend that explains the
 * comparison in plain English. The pill itself stays compact; the legend pops
 * over only when the user taps the info icon.
 *
 * Visibility rule (single source of truth — `shouldShowHighwayBadge`):
 *   • The badge renders whenever the gap is non-trivial (≥ HIGHWAY_BADGE_MIN_PP
 *     percentage points after rounding) in EITHER direction. A 0-2pp gap is
 *     statistical noise and an "about the same" pill just adds chrome, so we
 *     suppress it entirely instead of softening the wording.
 *   • Sign drives the wording AND the visual tone:
 *       deltaPP < 0 → "X% less highway than Google Maps"  (positive / pleasant)
 *       deltaPP > 0 → "X% more highway than Google Maps"  (warning / amber)
 *   • Massive gaps cap at HIGHWAY_BADGE_CAP_PP so we never claim
 *     "100% less/more highway" — an unverifiable absolute.
 *
 * Centralizing the gate here means callers can pass any `deltaPP` and trust
 * the badge to show only when its canonical phrase is both true and worth
 * saying.
 */
// Threshold + cap live in src/config/highwayBadge.ts so they can be tuned
// per-environment via VITE_HIGHWAY_BADGE_MIN_PP / VITE_HIGHWAY_BADGE_CAP_PP
// without editing this component. Re-exported here so existing call sites
// (`import { HIGHWAY_BADGE_MIN_PP } from "./RouteCard"`) keep working.
export { HIGHWAY_BADGE_MIN_PP, HIGHWAY_BADGE_CAP_PP } from "@/config/highwayBadge";
import { HIGHWAY_BADGE_MIN_PP, HIGHWAY_BADGE_CAP_PP } from "@/config/highwayBadge";

/** Returns true when a HighwayBadge should be rendered for the given delta.
 *  We only ever surface the badge when this route uses LESS highway than
 *  Google Maps (deltaPP < 0). We never want to claim "more highway than
 *  Google Maps" — that framing reads as a downside on a Pleasant pick and
 *  is not the story we want to tell. Round first so a -4.6 reads as 5 and
 *  qualifies, while -4.4 reads as 4 and is correctly suppressed. */
export const shouldShowHighwayBadge = (deltaPP: number | null | undefined): boolean => {
  if (deltaPP == null || !Number.isFinite(deltaPP)) return false;
  const rounded = Math.round(deltaPP);
  if (rounded >= 0) return false;
  return Math.abs(rounded) >= HIGHWAY_BADGE_MIN_PP;
};

export const HighwayBadge = ({
  deltaPP,
  size = "sm",
  thisHighwayPct,
  googleHighwayPct,
  routeName,
}: {
  deltaPP: number;
  size?: "xs" | "sm";
  /** Underlying raw percentages used to compute `deltaPP`. Optional — when
   *  provided, the popover shows the exact "Pleasant: X% · Google Maps: Y%"
   *  breakdown so users can see the full math. */
  thisHighwayPct?: number | null;
  googleHighwayPct?: number | null;
  /** Optional route label included in the screen-reader announcement so the
   *  spoken summary identifies which route the comparison belongs to. */
  routeName?: string;
}) => {
  // Auto-dismiss the popover when the user pans/zooms the map or scrolls —
  // otherwise the floating tooltip detaches from its anchor. Declared here,
  // above the early return below, so the hooks run in the same order on
  // every render (React Rules of Hooks).
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("map:interact", close);
    window.addEventListener("scroll", close, { capture: true, passive: true });
    window.addEventListener("resize", close);
    window.addEventListener("orientationchange", close);
    return () => {
      window.removeEventListener("map:interact", close);
      window.removeEventListener("scroll", close, { capture: true } as EventListenerOptions);
      window.removeEventListener("resize", close);
      window.removeEventListener("orientationchange", close);
    };
  }, [open]);

  // Defensive: even when callers forget the gate, the badge refuses to
  // render itself for trivial / non-finite deltas.
  if (!shouldShowHighwayBadge(deltaPP)) return null;
  const rounded = Math.round(deltaPP);
  const isMore = rounded > 0;
  const absRaw = Math.abs(rounded);
  const capped = Math.min(absRaw, HIGHWAY_BADGE_CAP_PP);
  // Positive framing for the calmer route: describe what it has MORE of — back
  // roads — never "less main roads" (Morgan: say what's there, not what isn't).
  // The busier Direct route keeps the honest "more main roads" heads-up.
  const roadsPhrase = isMore ? "more main roads" : "more back roads";
  const headline = `${capped}% ${roadsPhrase} than Direct`;
  // No `whitespace-nowrap` on the sm size — on iPhone (≤390px) the pill
  // wraps to two lines instead of overflowing the card horizontally.
  const cls =
    size === "xs"
      ? "text-[10px] px-2 py-1 min-h-[28px]"
      : "text-[11px] px-2.5 py-1 min-h-[32px]";
  // Color tone follows the direction: less highway = pleasant/primary,
  // more highway = warning/amber so the pill reads as a heads-up rather
  // than as praise. Both stay legible in light + dark.
  const toneCls = isMore
    ? "bg-amber-100 text-amber-900 hover:bg-amber-200 active:bg-amber-200/80 dark:bg-amber-900/40 dark:text-amber-100 dark:hover:bg-amber-900/60"
    : "bg-pleasant/10 text-primary hover:bg-pleasant/20 active:bg-pleasant/25";
  const infoCls = isMore ? "text-amber-700/80 dark:text-amber-200/80" : "text-primary/80";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          // Stable, device-agnostic label: the headline IS the meaning of
          // the badge, and the trailing "Show details." reads correctly
          // whether the user is tapping, clicking, or activating with a
          // keyboard / screen reader. Combined with aria-haspopup="dialog"
          // and aria-expanded below, AT announces "<headline>. Show
          // details. button, collapsed, opens dialog".
          aria-label={`${headline}. Show details.`}
          aria-haspopup="dialog"
          aria-expanded={open}
          // Stop propagation so activating the badge never bubbles up and
          // selects the underlying route card. We intercept the full
          // pointer/click/key sequence so screen-reader and keyboard users
          // get the same isolation as touch users.
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") e.stopPropagation();
          }}
          // WCAG 2.2 SC 2.4.7 (Focus Visible) + SC 1.4.11 (Non-text
          // Contrast): a 3px solid ring with a 2px offset gives ≥3:1
          // contrast against both the pleasant (light) and amber (light /
          // dark) tone backgrounds. Solid (not ring/2 with primary/50)
          // because translucent rings can fall under 3:1 on busy cards.
          className={`inline-flex items-center gap-1.5 font-semibold rounded-full tabular-nums transition-colors focus:outline-none focus-visible:ring-[3px] focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background cursor-pointer touch-manipulation ${toneCls} ${cls}`}
        >
          <span>{headline}</span>
          <Info className={`w-3.5 h-3.5 flex-shrink-0 ${infoCls}`} aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        role="dialog"
        aria-label="Main-roads comparison details"
        // Width adapts to small viewports (320–360px) so the popover never
        // overflows the screen edge. `max-h` + scroll guarantees the
        // dialog fits within the viewport even when both percentage rows
        // and the practice paragraph render — no excessive page scroll.
        className="w-[min(19rem,calc(100vw-1.25rem))] max-h-[min(22rem,75vh)] overflow-y-auto p-3 text-[11px] leading-snug focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Polite live region so screen readers announce the comparison when
            the popover opens, without interrupting any in-flight speech.
            Copy is intentionally compact: route name (when known), the
            headline gap, and the exact Pleasant vs Google percentages — no
            filler words. */}
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          {(() => {
            const hasPcts =
              Number.isFinite(thisHighwayPct ?? NaN) &&
              Number.isFinite(googleHighwayPct ?? NaN);
            const prefix = routeName ? `${routeName}: ` : "";
            const summary = `${capped}% ${roadsPhrase} than Direct`;
            const pcts = hasPcts
              ? `. Pleasant ${Math.round(thisHighwayPct as number)}%, Direct ${Math.round(googleHighwayPct as number)}%`
              : "";
            return `${prefix}${summary}${pcts}.`;
          })()}
        </div>
        {/* Title + threshold chip share one row to save vertical space. */}
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <div className="font-semibold text-[13px] leading-tight text-foreground">{headline}</div>
          <span className="shrink-0 inline-flex items-center rounded-full bg-muted/60 px-1.5 py-0.5 text-[9px] font-medium text-muted-foreground whitespace-nowrap">
            ≥{HIGHWAY_BADGE_MIN_PP}% gap
          </span>
        </div>
        <p className="text-muted-foreground">
          We compare how much of each route runs on big roads — highways and
          main arterials. This route uses about{" "}
          <span className="font-semibold text-foreground">
            {capped}% {roadsPhrase}{absRaw > HIGHWAY_BADGE_CAP_PP ? " (capped)" : ""}
          </span>{" "}
          than the Direct route.
        </p>
        {Number.isFinite(thisHighwayPct ?? NaN) && Number.isFinite(googleHighwayPct ?? NaN) ? (
          <dl
            className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5 rounded-md bg-muted/50 px-2 py-1.5 text-[10px]"
            aria-label="Exact main-road percentages"
          >
            <dt className="text-muted-foreground">Pleasant</dt>
            <dd className="text-right font-semibold tabular-nums text-foreground">
              {Math.round(thisHighwayPct as number)}%
            </dd>
            <dt className="text-muted-foreground">Direct</dt>
            <dd className="text-right font-semibold tabular-nums text-foreground">
              {Math.round(googleHighwayPct as number)}%
            </dd>
          </dl>
        ) : null}
        <p className="text-muted-foreground mt-1.5">
          {isMore
            ? "More of your drive is on big main roads (highways and arterials) — usually quicker, but louder and less scenic."
            : "More of your drive is on regular streets — usually calmer and more scenic, at the cost of a little extra time."}
        </p>
        <p className="text-muted-foreground mt-1.5 text-[10px]">
          Hidden when the gap is under {HIGHWAY_BADGE_MIN_PP}% — small differences are statistical noise.
        </p>
      </PopoverContent>
    </Popover>
  );
};

/**
 * ETA confidence indicator.
 *
 * The "+X min" pill compares this route to the fastest option, but small
 * differences are dominated by everyday traffic noise — a 2-minute gap on a
 * 30-minute trip is well inside the margin of error for any routing engine.
 * When that's the case we render a tiny "≈" chip next to the pill so users
 * know not to put too much weight on the time difference.
 *
 * Heuristic: a delta is "noisy" when it's ≤ 2 minutes OR ≤ ~5% of the
 * fastest-route duration; "approximate" when ≤ ~10%. Beyond that the
 * difference is large enough that traffic variability won't typically erase
 * it, and we suppress the chip entirely.
 */
type EtaConfidence = "confident" | "approximate" | "noisy";

export const etaConfidence = (
  extraMin: number,
  fastestDurationS: number | undefined,
): EtaConfidence => {
  if (extraMin <= 0) return "confident";
  const fastestMin = fastestDurationS != null ? fastestDurationS / 60 : 0;
  const ratio = fastestMin > 0 ? extraMin / fastestMin : 1;
  if (extraMin <= 2 || ratio < 0.05) return "noisy";
  if (ratio < 0.1) return "approximate";
  return "confident";
};

export const EtaConfidenceChip = ({
  extraMin,
  fastestDurationS,
  size = "sm",
}: {
  extraMin: number;
  fastestDurationS: number | undefined;
  size?: "xs" | "sm";
}) => {
  const confidence = etaConfidence(extraMin, fastestDurationS);
  if (confidence === "confident") return null;
  const isNoisy = confidence === "noisy";
  const label = isNoisy ? "Within traffic noise" : "Approximate";
  const explainer = isNoisy
    ? `This ${extraMin}-minute difference is small enough that real-world traffic variability could erase it — the two routes will often arrive at the same time.`
    : `Travel-time differences this small (under ~10% of the trip) are sensitive to real-time traffic and may shift either way.`;
  const cls =
    size === "xs"
      ? "text-[11px] px-2.5 py-1 min-h-[36px]"
      : "text-[11px] px-3 py-1 min-h-[40px]";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`ETA confidence: ${label}. Tap for details.`}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          className={`inline-flex items-center gap-1 font-medium rounded-full bg-muted text-foreground hover:bg-muted/80 active:bg-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background cursor-pointer touch-manipulation tabular-nums ${cls}`}
        >
          <span aria-hidden="true">≈</span>
          <span>{label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        className="w-64 text-xs leading-relaxed"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-semibold text-sm mb-1.5">ETA confidence: {label}</div>
        <p className="text-muted-foreground">{explainer}</p>
        <p className="text-muted-foreground mt-2 text-[11px]">
          The "+{extraMin} min" pill compares this route to the fastest option. Indicator only appears when the difference is small enough that traffic noise matters.
        </p>
      </PopoverContent>
    </Popover>
  );
};

/**
 * Plain-words "vs Google Maps" ETA pill.
 *
 * Wording rule:
 *   • |Δ| ≤ 2 min OR |Δ|/google < 0.05 → "≈ same time as Google Maps"
 *   • Δ > 2 min  → "X min longer than Google Maps"  (positive tone — small
 *     trade for a nicer route is a feature)
 *   • Δ < -2 min → null (badge omitted)
 *
 * Why we never claim "shorter than Google": Google is optimizing for the
 * fastest route, so by construction Pleasant alternatives cannot be
 * legitimately faster — if our number lands meaningfully below Google's,
 * that's engine variance (Mapbox/OSRM vs Google's speed model + traffic),
 * not a real win the user could take to the bank. The old "collapse shorter
 * into same" approach produced visible contradictions when two cards in the
 * same list both claimed "same as Google" at different ETAs. Now those
 * routes simply skip the comparison badge — we're not confident enough to
 * make the claim, so we don't.
 *
 * Returns null when Google's ETA is unavailable or when the Pleasant ETA is
 * meaningfully shorter than Google's.
 */
export const googleEtaVerdict = (
  thisDurationS: number,
  googleDurationS: number | null | undefined,
): {
  kind: "same" | "longer";
  minutes: number;
  /** Signed minute delta (this − Google), preserved even when the
   *  user-facing verdict collapses to "same". Lets opt-in callers expose
   *  the exact delta without re-running the math. */
  rawDeltaMin: number;
} | null => {
  if (googleDurationS == null || !Number.isFinite(googleDurationS) || googleDurationS <= 0) {
    return null;
  }
  if (!Number.isFinite(thisDurationS) || thisDurationS <= 0) return null;
  const deltaMin = Math.round((thisDurationS - googleDurationS) / 60);
  const googleMin = googleDurationS / 60;
  const ratio = Math.abs(deltaMin) / googleMin;
  if (Math.abs(deltaMin) <= 2 || ratio < 0.05) {
    return { kind: "same", minutes: 0, rawDeltaMin: deltaMin };
  }
  // Engine-variance guard: when our number lands meaningfully below Google's,
  // we can't honestly call it. Skip the badge for that card entirely.
  if (deltaMin < 0) return null;
  return {
    kind: "longer",
    minutes: deltaMin,
    rawDeltaMin: deltaMin,
  };
};

export const GoogleEtaBadge = ({
  thisDurationS,
  googleDurationS,
  thisDistanceM,
  googleDistanceM,
  size = "sm",
  /** When provided, controls whether the exact signed minute delta is
   *  appended to the verdict headline. When omitted, the badge becomes
   *  self-controlled — a tap toggles the exact-delta view, persisted to
   *  localStorage so the user's choice carries across cards & sessions. */
  showExactDelta,
}: {
  thisDurationS: number;
  googleDurationS: number | null | undefined;
  /** Distance of this route in meters — when paired with googleDistanceM,
   *  the chip surfaces the distance delta alongside the time delta so the
   *  user understands a long walking comparison is a longer path, not a
   *  slower pace (#72). */
  thisDistanceM?: number | null;
  googleDistanceM?: number | null;
  size?: "xs" | "sm";
  showExactDelta?: boolean;
}) => {
  const STORAGE_KEY = "pleasant.googleEtaExactDelta.v1";
  const [storedExact, setStoredExact] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try { return window.localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });
  const isControlled = showExactDelta !== undefined;
  const exact = isControlled ? !!showExactDelta : storedExact;

  const verdict = googleEtaVerdict(thisDurationS, googleDurationS);
  if (!verdict) return null;
  const copy = getRouteCopy().googleEta;
  const baseHeadline =
    verdict.kind === "same"
      ? copy.same
      : copy.longer(verdict.minutes);
  const formatDelta = (d: number): string => {
    if (d === 0) return "±0\u00A0min";
    if (d > 0) return `+${d}\u00A0min`;
    return `\u2212${Math.abs(d)}\u00A0min`;
  };
  // Distance-delta suffix (#72). Only appended when both distances are
  // present AND the gap is meaningful (≥0.2 mi). Critical for walking and
  // biking — a "+27 min" comparison reads as a pace mystery when it's
  // actually a path-length difference. Below the 0.2-mi threshold we
  // suppress the suffix so driving routes with near-identical distances
  // don't get noisy "(+0.1 mi)" trailing text.
  let distanceSuffix = "";
  if (
    typeof thisDistanceM === "number" && Number.isFinite(thisDistanceM) &&
    typeof googleDistanceM === "number" && Number.isFinite(googleDistanceM) &&
    googleDistanceM > 0
  ) {
    const deltaMi = (thisDistanceM - googleDistanceM) / 1609.344;
    if (Math.abs(deltaMi) >= 0.2) {
      const sign = deltaMi > 0 ? "+" : "−";
      // Aug 19 units audit: was hardcoded "mi". The threshold above stays in
      // miles (it's a judgement about noise, not display); the visible string
      // goes through fmtDistance so it honours the driver's chosen system.
      const formatted = fmtDistance(Math.abs(thisDistanceM - googleDistanceM));
      // Only add the longer-suffix for the "longer" verdict — for the
      // "same time" verdict we still want the user to see when their
      // route is meaningfully longer in distance ("same time as Google,
      // but +0.4 mi farther").
      distanceSuffix = verdict.kind === "same"
        ? `, ${sign}${formatted}`
        : ` (${sign}${formatted})`;
    }
  }
  const headline = exact
    ? `${baseHeadline}${distanceSuffix} (${formatDelta(verdict.rawDeltaMin)})`
    : `${baseHeadline}${distanceSuffix}`;
  const cls =
    size === "xs"
      ? "text-[10px] px-2 py-0.5 min-h-[22px]"
      : "text-[11px] px-2.5 py-0.5 min-h-[26px]";
  const toneCls = "bg-transparent text-primary";

  // Self-controlled mode: render as a button so the user can toggle the
  // exact delta in/out. Controlled mode keeps the old read-only span so
  // existing snapshots / tests that pass an explicit value stay stable.
  if (isControlled) {
    return (
      <span
        className={`inline-flex items-center font-semibold rounded-full tabular-nums ${toneCls} ${cls}`}
        title={headline}
        aria-label={headline}
      >
        {headline}
      </span>
    );
  }

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    const next = !storedExact;
    setStoredExact(next);
    try { window.localStorage.setItem(STORAGE_KEY, next ? "1" : "0"); } catch { /* ignore quota */ }
  };
  const aria = exact ? `${headline} — tap to hide exact delta` : `${headline} — tap to show exact delta`;
  return (
    <button
      type="button"
      onClick={toggle}
      className={`inline-flex items-center font-semibold rounded-full tabular-nums transition-colors hover:bg-primary/10 cursor-pointer ${toneCls} ${cls}`}
      title={aria}
      aria-label={aria}
      aria-pressed={exact}
    >
      {headline}
    </button>
  );
};

/**
 * Tiny "vs Google ▾" toggle next to the +X min pill. Lets users switch the
 * comparison baseline between Google's ETA, our fastest pick, and our
 * pleasant pick. Self-hides when fewer than two options are available.
 */
const BASELINE_LABEL: Record<"google" | "fastest" | "pleasant", string> = {
  google: "Fastest ETA",
  fastest: "our fastest",
  pleasant: "our pleasant",
};

export const BaselineToggle = ({
  mode,
  options,
  onChange,
  size = "sm",
}: {
  mode: "google" | "fastest" | "pleasant";
  options: { google?: number; fastest?: number; pleasant?: number };
  onChange: (mode: "google" | "fastest" | "pleasant") => void;
  size?: "xs" | "sm";
}) => {
  const available = (["google", "fastest", "pleasant"] as const).filter(
    (k) => options[k] != null,
  );
  if (available.length < 2) return null;
  const cls =
    size === "xs"
      ? "text-[10px] px-1.5 py-0.5 min-h-[20px]"
      : "text-[10px] px-2 py-0.5 min-h-[22px]";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Comparison baseline: ${BASELINE_LABEL[mode]}. Tap to change.`}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          className={`inline-flex items-center gap-1 font-medium rounded-full bg-muted/70 text-muted-foreground hover:bg-muted active:bg-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 cursor-pointer touch-manipulation ${cls}`}
        >
          <span>vs {BASELINE_LABEL[mode]}</span>
          <span aria-hidden="true">▾</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        className="w-60 p-2 text-xs"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-semibold text-[11px] uppercase tracking-wider text-muted-foreground px-2 pt-1 pb-1.5">
          Compare time to
        </div>
        <div role="radiogroup" aria-label="Comparison baseline" className="flex flex-col">
          {available.map((opt) => {
            const selected = opt === mode;
            const mins = Math.round((options[opt] as number) / 60);
            return (
              <button
                key={opt}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={(e) => { e.stopPropagation(); onChange(opt); }}
                onPointerDown={(e) => e.stopPropagation()}
                className={`flex items-center justify-between gap-3 px-2 py-1.5 rounded-md text-left transition-colors ${
                  selected ? "bg-primary/10 text-primary font-semibold" : "hover:bg-muted text-foreground"
                }`}
              >
                <span>{BASELINE_LABEL[opt]}</span>
                <span className="tabular-nums text-[11px] text-muted-foreground">{mins} min</span>
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-muted-foreground px-2 pt-2 pb-1 leading-snug">
          The "+X min" pill on every card is measured against this reference.
        </p>
      </PopoverContent>
    </Popover>
  );
};


// ---------------------------------------------------------------------------
// ScoreBreakdown — collapsible, surfaces the underlying scoring numbers so
// users can see WHY one route ranked above another. Shows the four scoring
// dimensions with bars (calm / scenic / preference / community), the
// composite pleasant score, and an explicit highway-influence callout that
// names any epic scenic highway and quantifies high-traffic exposure
// (with a delta vs the Direct route when available).
// ---------------------------------------------------------------------------
type ScoreBreakdownProps = {
  scores: ClassifiedRoute["scores"];
  features: ClassifiedRoute["features"];
  fastestFeatures?: ClassifiedRoute["features"];
  highTrafficM: number;
  epicHighwayM: number;
  epicHighwayLabel?: string;
  totalDistanceM: number;
  isBaseline: boolean;
};

const ScoreBar = ({ label, value, tone }: { label: string; value: number; tone: string }) => {
  const pct = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] text-foreground/75">
        <span>{label}</span>
        <span className="tabular-nums font-medium text-foreground/85">{pct}</span>
      </div>
      <div
        className="mt-0.5 h-1.5 rounded-full bg-muted overflow-hidden"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${label} score ${pct} of 100`}
      >
        <div className={`h-full ${tone} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const ScoreBreakdown = ({
  scores, features, fastestFeatures,
  highTrafficM, epicHighwayM, epicHighwayLabel,
  totalDistanceM, isBaseline,
}: ScoreBreakdownProps) => {
  // Only suppress when the score is missing entirely. The previous ≤20 hide
  // was added to avoid "Pleasant 3/100" on beautiful drives, but it backfired
  // because the Fastest card then had no Pleasant score at all — leaving the
  // user with no way to compare cards on the dimension that matters to the
  // brand. The score floor inside scoring.ts now keeps low scores from
  // reading as nonsense, so showing the number is more honest than hiding
  // it.  Real-world feedback: "we should have pleasant rating on all the
  // fastest routes as well as the other info on other cards — it should
  // very very rarely be better than the other routes."
  if (!Number.isFinite(scores?.pleasant)) {
    return null;
  }
  const fmtMi = (m: number) => {
    const mi = m / 1609.344;
    return mi < 0.2 ? `${Math.max(10, Math.round((m / 0.3048) / 10) * 10)} ft` : `${mi < 10 ? mi.toFixed(1) : Math.round(mi)} mi`;
  };
  const hwyPct = Math.round(features.highwayPct ?? 0);
  const baseHwyPct = fastestFeatures ? Math.round(fastestFeatures.highwayPct ?? 0) : null;
  const deltaPP = baseHwyPct != null && !isBaseline ? hwyPct - baseHwyPct : null;
  const highTrafficMi = highTrafficM / 1609.344;
  const epicMi = epicHighwayM / 1609.344;

  // Highway-influence narrative — explicitly names what drove the result.
  const highwayLines: { text: string; tone: "positive" | "neutral" | "caveat" }[] = [];
  if (epicHighwayM > 1000 && epicHighwayLabel) {
    highwayLines.push({
      text: `Routes ${epicMi < 10 ? epicMi.toFixed(1) : Math.round(epicMi)} mi along ${epicHighwayLabel} (legendary scenic highway) — boosts scenic & pleasant scores.`,
      tone: "positive",
    });
  }
  if (highTrafficM > 0) {
    const pctOfRoute = totalDistanceM > 0 ? Math.round((highTrafficM / totalDistanceM) * 100) : 0;
    highwayLines.push({
      text: `${fmtMi(highTrafficM)} (${pctOfRoute}%) on motorway / primary arterials — penalizes the calm score.`,
      tone: pctOfRoute >= 25 ? "caveat" : "neutral",
    });
  }
  if (deltaPP != null) {
    if (deltaPP <= -5) {
      highwayLines.push({
        text: `${Math.abs(deltaPP)} pp less highway exposure than the Direct route — main reason this scored higher on calm.`,
        tone: "positive",
      });
    } else if (deltaPP >= 10) {
      highwayLines.push({
        text: `+${deltaPP} pp more highway than the Direct route — saves time but lowered calm.`,
        tone: "caveat",
      });
    }
  }
  if (highwayLines.length === 0) {
    highwayLines.push({
      text: hwyPct === 0
        ? "No motorway or primary-arterial segments detected on this route."
        : `${hwyPct}% of corridor on busier roads — minor influence on scoring.`,
      tone: "neutral",
    });
  }

  // 1–2 line headline summary: name the strongest scoring lever + the most
  // important highway-influence note. Always visible; full bars + narrative
  // live behind "See details".
  const leverEntries: { label: string; value: number }[] = [
    { label: "calm",       value: scores.calm },
    { label: "scenic",     value: scores.scenic },
    { label: "preference", value: scores.preferenceMatch },
    { label: "community",  value: scores.community },
  ];
  const topLever = leverEntries.reduce((a, b) => (b.value > a.value ? b : a));
  const headlineLine = highwayLines.find((l) => l.tone === "positive")
    ?? highwayLines.find((l) => l.tone === "caveat")
    ?? highwayLines[0];

  // Plain-language facts for the clean details panel — built from the same
  // always-present feature data the card face uses. No 0–100 score bars:
  // Morgan's redesign moves the details away from raw numbers toward
  // "what's actually on this route."
  const resid = Math.round(features.residentialPct ?? 0);
  const hwyPctNum = Math.round(features.highwayPct ?? 0);
  const majorPct = Math.round(features.majorRoadPct ?? 0);
  const roadBits: string[] = [];
  if (resid >= 8) roadBits.push(`${resid}% quiet residential`);
  if (majorPct >= 8) roadBits.push(`${majorPct}% main roads`);
  if (hwyPctNum >= 5) roadBits.push(`${hwyPctNum}% main roads`);
  const roadMix = roadBits.join(" · ") || "a mix of local roads";

  const waterList = features.waterNames ?? [];
  const parkList = [...(features.parkNames ?? []), ...(features.natureNames ?? [])];
  const historicList = features.historicNames ?? [];
  const landmarkList = features.landmarkNames ?? [];
  const treeKm = features.treeLinedKm ?? 0;

  type Fact = { label: string; value: string };
  const facts: Fact[] = [{ label: "Roads", value: roadMix }];
  if (waterList.length) facts.push({ label: "Along", value: waterList.slice(0, 3).join(", ") });
  if (parkList.length) facts.push({ label: "Parks", value: parkList.slice(0, 3).join(", ") });
  if (historicList.length) facts.push({ label: "Historic", value: historicList.slice(0, 2).join(", ") });
  if (landmarkList.length) facts.push({ label: "Past", value: landmarkList.slice(0, 2).join(", ") });
  if (treeKm >= 0.5) facts.push({ label: "Tree-lined", value: `${(treeKm / 1.60934).toFixed(1)} mi` });

  return (
    <details className="mt-1 group">
      <summary
        className="list-none cursor-pointer select-none rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        aria-label="See route details"
      >
        <div className="flex items-center justify-end">
          <span className="text-[13px] font-medium text-primary inline-flex items-center gap-1 min-h-11 px-1 group-open:hidden">
            See details <span aria-hidden="true">▾</span>
          </span>
          <span className="text-[13px] font-medium text-primary items-center gap-1 min-h-11 px-1 hidden group-open:inline-flex">
            Hide <span aria-hidden="true">▴</span>
          </span>
        </div>
      </summary>

      <div className="mt-1 rounded-xl border border-border/60 bg-muted/20 p-3 space-y-2">
        {facts.map((f, i) => (
          <div key={i} className="flex items-baseline gap-3 text-[13px] leading-snug">
            <span className="text-muted-foreground w-20 shrink-0">{f.label}</span>
            <span className="text-foreground">{f.value}</span>
          </div>
        ))}
        {highwayLines.find((l) => l.tone === "caveat") && (
          <div className="flex items-baseline gap-3 text-[13px] leading-snug">
            <span className="text-amber-700 dark:text-amber-400 w-20 shrink-0">Heads up</span>
            <span className="text-foreground/85">{highwayLines.find((l) => l.tone === "caveat")!.text}</span>
          </div>
        )}
      </div>
    </details>
  );
};


// ---------------------------------------------------------------------------
// SameAsFastestPanel — shown on the Pleasant card when the pleasant pick
// collapsed onto the fastest route. Tells the user *why* (no alternatives,
// alternatives shared the same polyline, alternatives were filtered out, or
// nothing cleared the pleasantness bar) instead of leaving them to wonder
// why two cards have identical ETAs and distance.
// ---------------------------------------------------------------------------
const SameAsFastestPanel = ({
  reason,
  relax,
}: {
  reason?: ClassifiedRoute["sameAsFastestReason"];
  relax?: Props["relaxHints"];
}) => {
  const code = reason?.code ?? "unknown";
  const candidateCount = reason?.candidateCount ?? 0;
  const altScore = reason?.bestAltPleasantScore;
  const minScore = reason?.minPleasantScore;
  const overlap = reason?.bestAltOverlap;

  const headline: Record<NonNullable<ClassifiedRoute["sameAsFastestReason"]>["code"], string> = {
    no_alternatives:  "The routing engine returned only one viable line for this trip.",
    polyline_match:   "Every alternative we found follows the same roads as the Direct route.",
    filtered_out:     "Alternatives existed but were excluded by your route filters.",
    score_degenerate: "We did find alternatives, but none scored high enough to call “pleasant.”",
    unknown:          "The Direct route is also the most pleasant one we could find here.",
  };

  const detail: string | null = (() => {
    switch (code) {
      case "no_alternatives":
        return "Couldn't generate a second route — likely because this trip is short or constrained (one bridge, one ferry, a single corridor). There's no second line to compare against.";
      case "polyline_match":
        return overlap != null
          ? `The best alternative shared ${Math.round(overlap * 100)}% of its polyline with the Direct route, so it would feel identical on the map.`
          : "Alternatives existed but their polylines overlap the Direct route too closely to be a meaningfully different choice.";
      case "filtered_out":
        return "Your active filters (avoid highways, duration cap, etc.) removed every alternative. Loosening them — or switching to Scenic tolerance — would let those candidates back in.";
      case "score_degenerate":
        if (altScore != null && minScore != null) {
          return `The best alternative scored ${Math.round(altScore)}/100 on pleasantness — below the ${Math.round(minScore)} bar we require to call a route “pleasant.” Showing it would mislead.`;
        }
        return "The alternatives we evaluated didn't have enough parks, water, quiet streets, or community signal to outscore the Direct route.";
      default:
        return null;
    }
  })();

  // Pick the single most-actionable relax suggestion. Avoid-highways is
  // usually the biggest exclusion lever, so it wins when both are
  // available; otherwise, suggest bumping the detour tolerance up one
  // notch (fast → balanced → scenic).
  const action: { label: string; hint: string; onClick: () => void } | null = (() => {
    if (code !== "filtered_out" || !relax) return null;
    if (relax.hasNoHighways && relax.onRemoveNoHighways) {
      return {
        label: "Allow highways",
        hint: "Drops the “avoid highways” filter so freeway alternatives can compete.",
        onClick: relax.onRemoveNoHighways,
      };
    }
    if (relax.detourTolerance && relax.detourTolerance !== "scenic" && relax.onIncreaseTolerance) {
      const next: DetourTolerance = relax.detourTolerance === "fast" ? "balanced" : "scenic";
      return {
        label: `Switch to ${TOLERANCE_LABELS[next]} tolerance`,
        hint: `Raises the duration cap so longer-but-prettier alternatives clear the gate.`,
        onClick: relax.onIncreaseTolerance,
      };
    }
    return null;
  })();

  return (
    <div className="mt-2 rounded-xl border border-primary/25 bg-primary/5 px-3 py-2.5 text-[12px] leading-snug">
      <div className="flex items-start gap-2">
        <Info className="w-3.5 h-3.5 mt-0.5 text-primary shrink-0" aria-hidden="true" />
        <div className="space-y-1.5 min-w-0 flex-1">
          <div className="font-semibold text-foreground/90">
            Why same ETA &amp; distance as Fastest?
          </div>
          <p className="text-foreground/80">{headline[code]}</p>
          {detail && <p className="text-foreground/70">{detail}</p>}
          {action && (
            <div className="pt-1.5">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); action.onClick(); }}
                onPointerDown={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground text-[11px] font-semibold px-3 py-1.5 shadow-sm hover:bg-primary/90 active:bg-primary/85 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background touch-manipulation"
                aria-label={`${action.label}. ${action.hint}`}
              >
                <Sparkles className="w-3 h-3" aria-hidden="true" />
                {action.label}
              </button>
              <p className="mt-1 text-[11px] text-foreground/60">{action.hint}</p>
            </div>
          )}
          <div className="text-[11px] text-foreground/55 pt-1">
            Diagnostics: {candidateCount} distinct geometr{candidateCount === 1 ? "y" : "ies"} evaluated
            {altScore != null && (
              <> · best alt pleasantness {Math.round(altScore)}/100</>
            )}
            {overlap != null && (
              <> · top overlap {Math.round(overlap * 100)}%</>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};



// ---------------------------------------------------------------------------
// "FAST & PLEASANT" IS A CLAIM, AND A CLAIM NEEDS ITS INPUTS MEASURED
// (rules 20/22; release spec item 1, Aug 11). Pure and exported so the
// regression test can hold the line: unknown metrics must never earn the
// label by falling back to zero, and metrics borrowed from a DIFFERENT
// route's geometry (the Google carrier inherits the engine-fastest
// features, or EMPTY_FEATURES zeros) are not measurements of this road —
// both are finite numbers, which is exactly why Number.isFinite alone
// (bcc15cc9) could not finish the job.
// ---------------------------------------------------------------------------
export interface FastPleasantInputs {
  /** highwayPct AND majorRoadPct are actual numbers (bcc15cc9's guard). */
  featuresMeasured: boolean;
  /** False when the features describe some other geometry (Google carrier). */
  measuredOnThisGeometry: boolean;
  nonEpicHighwayPct: number;
  majorRoadPct?: number;
  calmScore?: number;
  pleasantScore?: number;
  throughPark?: boolean;
  carFreeKm?: number;
  residentialPct?: number;
  waterNamesCount?: number;
  treeLinedKm?: number;
}

export function computeFastIsPleasant(i: FastPleasantInputs): boolean {
  if (!i.featuresMeasured) return false; // unknown ≠ zero (rule 20)
  if (!i.measuredOnThisGeometry) return false; // borrowed numbers aren't measurements
  return (
    i.nonEpicHighwayPct < 30 &&
    (i.majorRoadPct ?? 0) < 50 &&
    ((i.majorRoadPct ?? 0) < 25 ||
      (i.calmScore ?? 0) >= 45 ||
      (i.pleasantScore ?? 0) >= 45 ||
      !!i.throughPark ||
      (i.carFreeKm ?? 0) >= 0.3 ||
      (i.residentialPct ?? 0) >= 15 ||
      (i.waterNamesCount ?? 0) > 0 ||
      (i.treeLinedKm ?? 0) >= 0.3)
  );
}

export const RouteCard = ({ route, selected, onClick, fastestDuration, profile, fastestPleasantScore, detourCount = 0, recommended, fastestFeatures, whyOpen, onToggleWhy, whyContent, onOpenDetails, onOpenDetours, detourBadgeCount = 0, detourPanel, optionNumber, googleHighwayDeltaPP, thisHighwayPct, googleHighwayPct, googleOverride, baselineMode, baselineOptions, onBaselineModeChange, travelMode, googleDurationS, googleDistanceM, trafficDurationS, relaxHints, isOnlyOption, noMiddleOption, peers, fastestRouteCoords }: Props) => {
  let meta = META[route.classification];

  // GOOGLE CARRIER SHORT-CIRCUIT: when this card carries the literal
  // Google Maps route (the always-on comparison slot), the label,
  // subtitle, and icon must read as "Google Maps" — full stop. We skip
  // the epic-highway promotion AND the same-as-fastest treatment so the
  // slot is unambiguously Google's pick, never disguised as one of our
  // pleasant variants.
  const isGoogle = isGoogleCarrier(route);

  // The one clean sentence + time trade that leads the card (Morgan's
  // redesign: "very clean one-sentence why, then click for more"). Built
  // from the always-present feature/road-character data so it never
  // fabricates — see utils/routeReason.ts. Skipped for the Google carrier,
  // whose identity is fixed as "Google Maps".
  // (Morgan) Build the reason for the Google/Fast card too, so it lists the big
  // roads it puts you on ("Mostly via I-95 S") and can be compared with the other
  // cards. Its isFastest branch produces the road list from the route's steps;
  // there's no divergence story (it IS the direct line), so this stays a clean
  // "here's the highway you'll be on" sentence.
  // REMEMBERED BETWEEN REDRAWS (Aug 20, measured). buildRouteReason walks
  // every point of this route against every segment of the direct line to
  // find where they part company - 400k to 1.2M operations, benchmarked at
  // 59-126 ms per card. It ran in the render body, and a single search
  // redraws the cards about six times as results stream in, so the same
  // arithmetic was being redone ~12 times per search: up to 1.5 s of frozen
  // screen on a laptop and several times that on a phone.
  //
  // The dependency list is deliberately NOT [peers]: Index rebuilds that
  // array with .map() on every render, so depending on it would bust the
  // cache every time and change nothing. peersKey captures the only parts of
  // a peer that can alter the sentence - its time, distance, dominant road
  // and card slot - as a cheap string, so a real change still recomputes.
  const peersKey = (peers ?? [])
    .map((p) => `${p.durationS}|${p.distanceM}|${p.dominantRoad ?? ""}|${p.classification ?? ""}`)
    .join(";");
  const reason = useMemo(
    () => buildRouteReason(route, fastestDuration, peers, fastestRouteCoords),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [route, fastestDuration, peersKey, fastestRouteCoords],
  );

  // EPIC-HIGHWAY OVERRIDE: a route that runs a meaningful stretch along a
  // legendary coastal/scenic highway (PCH, A1A, Blue Ridge, etc.) — or any
  // route the engine surfaced as the recommended pick — must never read as
  // the plain "Direct route". Promote its label so the title/subtitle match
  // the user's intent. (Skipped for the Google carrier — its identity is
  // fixed.)
  const _epicMetersForLabel = route.route.epicHighwayMeters ?? 0;
  const _epicLabelForLabel = route.route.epicHighwayLabels?.[0];
  const _hasEpicStretch = !isGoogle && _epicMetersForLabel > 1000 && !!_epicLabelForLabel;
  if (!isGoogle && route.classification === "fastest" && (_hasEpicStretch || (recommended ?? false))) {
    const promotedLabel = route.variantLabel ?? _epicLabelForLabel ?? "Recommended";
    const promotedSub = route.variantSub
      ?? (_epicLabelForLabel ? `Along ${_epicLabelForLabel}` : "Best fit for your preferences");
    meta = { ...meta, label: promotedLabel, sub: promotedSub };
  }

  // When this card is the "Direct route" baseline AND we have Google
  // Directions data for the same A→B, surface Google's numbers so the card
  // is a real side-by-side comparison. The Google carrier already IS that
  // route — its own duration/distance are Google's numbers, so we never
  // need the override path.
  const useGoogleBaseline =
    !isGoogle &&
    route.classification === "fastest" &&
    !_hasEpicStretch &&
    !(recommended ?? false) &&
    !!googleOverride;
  // Prefer the live-traffic ETA when we have one — otherwise we end up showing
  // Pleasant-engine optimistic times alongside Google's traffic-aware time
  // for the Fastest card, which makes Wander look faster than Fastest even
  // when Google's chosen route would actually be quicker in real conditions.
  // Single source of truth: traffic-adjusted minutes for every card.
  const engineDuration = useGoogleBaseline ? googleOverride!.durationS : route.route.duration;
  // ETA POLICY, FINAL (release spec item 2, Aug 11). The whole P0 →
  // revert → re-revert story now lives in src/utils/etaPolicy.ts; the short
  // version: route.duration was never wrong (docs/OPEN_ETA_BUG.md), the 5h
  // cards were the positional Pleasant door showing the wrong ROUTE (fixed,
  // 2862223f), and the trafficDurationS override restored by 88b61353 was a
  // band-aid for that misdiagnosis. One layer logged "ignored second traffic
  // estimate" while this one displayed it — that contradiction ends here:
  // the card shows route.duration, and the secondary estimate is logged as
  // what it is.
  const displayDuration = plannedCardEtaSeconds(engineDuration, trafficDurationS);
  if (
    typeof trafficDurationS === "number" &&
    Number.isFinite(trafficDurationS) &&
    trafficDurationS > 0 &&
    Math.round(trafficDurationS) !== Math.round(engineDuration)
  ) {
    // eslint-disable-next-line no-console
    console.log(
      `[eta-secondary] duration=${Math.round(trafficDurationS)}s ` +
        `(${Math.round(trafficDurationS / 60)}min) ignored=true — ` +
        `plan is canonical this release (class=${route.classification ?? "?"})`,
    );
  }
  // eslint-disable-next-line no-console
  console.log(
    `[eta-plan] routeDuration=${Math.round(engineDuration / 60)}min ` +
      `distance=${((useGoogleBaseline ? googleOverride!.distanceM : route.route.distance) / 1000).toFixed(1)}km ` +
      `impliedKph=${Math.round(
        ((useGoogleBaseline ? googleOverride!.distanceM : route.route.distance) / 1000) /
          Math.max(0.01, engineDuration / 3600),
      )} ` +
      `class=${route.classification ?? "?"} ` +
      `traffic=${
        typeof trafficDurationS === "number" && trafficDurationS > 0
          ? `${Math.round(trafficDurationS / 60)}min`
          : "none"
      } shown=${Math.round(displayDuration / 60)}min`,
  );
  const displayDistance = useGoogleBaseline ? googleOverride!.distanceM : route.route.distance;

  // ---------------------------------------------------------------------
  // TIME TRADE, MEASURED AGAINST WHAT IS ACTUALLY ON SCREEN
  // (card-copy audit, Aug 24 — Morgan: "If Pleasant is +2 minutes, say that.")
  //
  // routeReason.ts deliberately does NOT build this line, and it was right not
  // to: its baseline is the `fastestDuration` prop, which is the ENGINE's
  // fastest and can disagree with the Fast card the driver is looking at. On
  // Asheville → Hendersonville that prop put the Pleasant card at +2 min while
  // the Fast card rendered beside it made the real gap +13 min.
  //
  // So the baseline here is the smallest DISPLAYED duration among this card
  // and its on-screen peers — the same numbers the driver is comparing. If we
  // have no peers we say nothing rather than guess.
  const _peerShown = (peers ?? [])
    .map((p) => p.durationS)
    .filter((n): n is number => typeof n === "number" && Number.isFinite(n) && n > 0);
  const _fastestShownS = _peerShown.length
    ? Math.min(displayDuration, ..._peerShown)
    : null;
  const _fastestPeer = _peerShown.length
    ? (peers ?? []).reduce((best, p) =>
        typeof p.durationS === "number" && p.durationS > 0 && (!best || p.durationS < best.durationS) ? p : best,
      null as PeerRoute | null)
    : null;
  const _extraDistanceM =
    _fastestPeer && typeof _fastestPeer.distanceM === "number"
      ? displayDistance - _fastestPeer.distanceM
      : undefined;
  const _tradeRaw =
    _fastestShownS != null && !isGoogle
      ? buildTradeoff(
          displayDuration,
          _fastestShownS,
          displayDuration <= _fastestShownS + 1,
          _extraDistanceM,
        )
      : "";
  // "The quickest way" is not a trade — it is the absence of one, and on the
  // Fast card it reads as filler beside the ETA it restates. Only an actual
  // cost (or a genuine saving) earns the slot.
  const timeTradeLine = _tradeRaw === "The quickest way" ? "" : _tradeRaw;
  if (useGoogleBaseline) {
    meta = { ...meta, sub: "Same path the Direct route would give" };
  }
  if (isGoogle) {
    // Card identity stays Google's route (geometry + ETA + distance), but
    // the visible label reads as the localized "Direct route" so it sits
    // alongside Pleasant/Wander as a peer option rather than an external
    // brand callout. The "≈ same time as Google Maps" pill rendered below
    // is what makes the comparison explicit — the title doesn't need to.
    const directCopy = getRouteCopy().classification.fastest;
    meta = {
      ...meta,
      Icon: MapIcon,
      label: directCopy.label,
      sub: directCopy.sub,
    };
  }

  // Highlight on rescore: when this route's pleasant score shifts (e.g. from
  // a mid-trip toggle rating), pulse the card and show a transient ±delta
  // chip so the user can see the impact of their input.
  const prevPleasantRef = useRef<number | null>(null);
  const [scoreFlash, setScoreFlash] = useState<{ delta: number; key: number } | null>(null);
  // Expand/collapse for the curated-road blurb (shown truncated by default).
  const [blurbOpen, setBlurbOpen] = useState(false);
  // Expand/collapse for the full list of pleasant roads ("and N more").
  const [roadsOpen, setRoadsOpen] = useState(false);
  useEffect(() => {
    const cur = route.scores.pleasant ?? 0;
    const prev = prevPleasantRef.current;
    prevPleasantRef.current = cur;
    if (prev == null) return; // first render — no flash
    const delta = Math.round(cur - prev);
    if (Math.abs(delta) < 1) return; // ignore sub-point noise
    const flash = { delta, key: Date.now() };
    setScoreFlash(flash);
    const t = window.setTimeout(() => {
      setScoreFlash((cur) => (cur && cur.key === flash.key ? null : cur));
    }, 1800);
    return () => window.clearTimeout(t);
  }, [route.scores.pleasant]);
  // When an optionNumber is provided, the card title becomes "Option N".
  // The descriptor underneath (variantSub or meta.sub) still tells the user
  // what's actually distinctive about the route — just without claiming it's
  // "the fastest" or "the most direct" when that may not literally be true.
  //
  // ONLY-OPTION FRAMING: when the engine has nothing meaningfully different
  // from Google to offer on this trip, the one card we surface gets framed
  // as "the most direct way — no calmer route on this trip" so we never
  // misrepresent a highway slog as a pleasant alternative.
  //
  // The previous `_shouldDemoteToDirect` gate (relabel pleasant→Direct when
  // sameAsFastest is true) is gone: with Google no longer rendered as a
  // card, pleasant routes can stay titled "Pleasant route". If the engine's
  // pleasant pick collapses onto the direct path, isOnlyOption takes over
  // and frames the single surfaced card honestly.
  // "Pleasant route" follows the RECOMMENDATION, not the engine's compile-
  // time classification. The engine assigns "pleasant" / "scenic" based on
  // raw duration; the recommendation re-scores with live traffic and can
  // pick a different card. When that happens, the FOR YOU card must be
  // the one titled "Pleasant route" — otherwise the user sees a card
  // labelled "Pleasant" that is in fact slower AND lower-scoring than the
  // recommended Adventure card next to it. Real-drive note (#45):
  // "pleasant takes you through town during rush hour — pleasant is in
  // fact longer than adventure."
  //
  // Non-recommended cards keep their variant labels (Adventure, Wander,
  // Riverside, etc.) so each card still tells you what it IS.
  // Card titles follow the CLASSIFIER, not a highway-percentage heuristic
  // (Morgan: "there should only be one card called the fast route and it should
  // genuinely be the fastest"). So exactly one card — the one classified
  // `fastest` — reads "Fast route"; pleasant and scenic keep their own labels
  // even on a parkway. Keeping ugly highways OUT of the pleasant slot is the
  // SCORER's job (curated avoid-road penalty + great-road boost), not a label
  // hack here.
  const isRecommended = recommended ?? (route.classification === "pleasant");
  const isPleasant = isRecommended;
  const pleasantBrandLabel = META.pleasant.label;
  // Non-recommended fallback CANNOT collide with the recommended brand
  // label. When categorizePleasant didn't earn the route a landscape
  // variant (coast, river, mountain, etc.), the previous fallback
  // (meta.label) landed on "Pleasant route" too — giving the user TWO
  // cards both titled "Pleasant route". Real-drive note (#46): the
  // alternative card was called "Pleasant route" right alongside the
  // recommended one. Use a neutral "Alternate route" instead when no
  // variantLabel exists.
  const fallbackBrandLabel =
    route.variantLabel
      ?? (route.classification === "pleasant" || meta.label === pleasantBrandLabel
            ? "Alternate route"
            : meta.label);
  // The "long way" (scenic) is ALWAYS the Scenic card — never titled "Pleasant
  // route", even if the recommender highlighted it (Morgan: a +51-min detour
  // shouldn't read as the Pleasant pick). Pleasant is reserved for the genuine
  // near-direct middle-ground.
  // A HIGHWAY SLOG NEVER WEARS THE PLEASANT NAME (Morgan, Aug 2: "why is
  // this highway road called pleasant" — Portland→SF, nine and a half hours
  // of I-5, titled "Pleasant route · FOR YOU" because the recommendation-
  // follows-branding rule forced it). The subtitle was honest ("Fastest —
  // mostly main roads"); the header lied. When the recommended card is the
  // fastest-classified route and it's mostly highway, it keeps its real
  // name. FOR YOU can point at it; it cannot rename it.
  const recommendedIsHighwaySlog =
    route.classification === "fastest" &&
    (route.features?.highwayPct ?? 0) >= 40;
  const _brandLabel =
    route.classification === "scenic"
      ? META.scenic.label
      : isRecommended && !recommendedIsHighwaySlog
        ? pleasantBrandLabel
        : fallbackBrandLabel;
  // (#135) Title override for sameAsFastest cards. Under the per-tolerance
  // classifyRoutes rewrite (#123), Pleasant or Scenic can collapse onto the
  // fastest route and surface it with `classification: "pleasant"|"scenic"`
  // + `sameAsFastest: true`. Without an override, BOTH that card and the
  // real Pleasant alternative end up titled "Pleasant route" — visually
  // indistinguishable in the card list. Force the collapsed card to read
  // "Most direct here" so the user can tell them apart.
  //
  // (#151 / #152) New `sameAsClass` field is set when a card explicitly
  // collapses onto another slot — Pleasant onto Fastest (margin not met),
  // or Scenic onto Pleasant (polyline overlap ≥ 0.85). When present, the
  // copy literally tells the user "Same as the [Fastest/Pleasant] route"
  // so they understand why two cards draw the same line. This supersedes
  // the generic "Most direct here" copy.
  const sameAsClass = route.sameAsClass;
  const sameAsLabel =
    sameAsClass === "fastest"
      ? "Same as the Fastest route"
      : sameAsClass === "pleasant"
        ? "Same as the Pleasant route"
        : null;
  const sameAsSub =
    sameAsClass === "fastest"
      ? "No calmer or more scenic option on this trip"
      : sameAsClass === "pleasant"
        ? "No additional scenic option on this trip"
        : null;
  // FAST + PLEASANT: when the single best route IS the fastest AND it's genuinely
  // calm (not a highway slog), frame it positively — "Fast and pleasant" — never
  // the negative "no calmer route" (Morgan). A named park it runs through gets
  // called out too ("this takes you literally through a forest — Google won't").
  const _ff = route.features;
  // Only claim "through {park}" for a park the route VERIFIABLY runs through —
  // the walk-park generator's own parkName, or the `is_in` containment result
  // (throughParkNames). Never a park merely passed near, or a loose nature
  // point (Morgan: "only IF that's the actual name of the actual park").
  const fastThroughPark =
    route.route.parkName ?? _ff?.throughParkNames?.[0];
  // The route is "pleasant" when the ENGINE scored it that way — its calm /
  // pleasant score is what drives the positive chips on the card ("pleasant
  // local roads", "quieter residential streets"), so trust that rather than a
  // brittle residential-% threshold (a through-road like Sleepy Hollow Rd isn't
  // tagged residential yet is genuinely calm). Highway-heavy routes are gated
  // out so a real highway slog never reads as pleasant.
  // "Fast & pleasant" requires the route to be GENUINELY calm — applied the
  // same for walking, biking AND driving (Morgan: a route that isn't actually
  // pleasant must NOT claim it, whatever the mode). The "is it the most
  // pleasant?" part is enforced by isOnlyOption at the usage site (this label is
  // only used when there's no more-pleasant alternative). Genuine calm = not on
  // a highway, not dominated by main-road arterials, and showing at least one
  // real calm signal (mostly side streets, a verified park/trail, water, trees,
  // residential, or a decent calm/pleasant score).
  // EPIC PARKWAYS COUNT AS PLEASANT, NOT AS HIGHWAY (Morgan, Aug 9: "remember
  // the fastest route is the pleasant route on this one").
  //
  // Briarcliff → Rhinebeck runs the Taconic State Parkway: motorway-class in
  // OSM, so `highwayPct` reads 100 — and this test demanded < 30, which meant
  // the card could never say "Fast & pleasant" on precisely the road where it
  // is most true. The scorer already knew (epicHighways.ts:68 lists the Taconic,
  // "the de-facto pleasant route for east-Hudson trips"; scoring.ts:3634 says it
  // again) and gave that route pleasant=76. Rule 2's exemption — on an epic
  // highway the highway IS the attraction — lived in the engine and had never
  // been told to the copy. One rule, two layers, only one of them knowing:
  // rule 20, in the display.
  const _epicM = (route.route as { epicHighwayMeters?: number } | undefined)?.epicHighwayMeters ?? 0;
  const _routeM = (route.route as { distance?: number } | undefined)?.distance ?? 0;
  const _epicSharePct = _routeM > 0 ? (_epicM / _routeM) * 100 : 0;
  // Motorway miles that are a designated parkway/byway don't count against calm.
  const _nonEpicHighwayPct = Math.max(0, (_ff?.highwayPct ?? 0) - _epicSharePct);
  // UNKNOWN IS NOT ZERO (rule 20) — RELEASE BLOCKER, Aug 11.
  //
  // Runtime evidence: the Direct classifier logged `directHwy=100%`, and the
  // canonical Fastest card then logged `src=unknown hwy=0% fastIsPleasant=true`
  // on the same trip. The metadata is lost when the canonical Fastest route
  // enters the card set, and every `?? 0` below then reads that absence as
  // "no highway at all":
  //     _nonEpicHighwayPct = 0  → passes `< 30`
  //     majorRoadPct       = 0  → passes `< 50`
  //     majorRoadPct       = 0  → passes `< 25` in the third group
  // Three gates, all satisfied by nothing being measured. A 100%-motorway route
  // wears "Fast & pleasant" because we failed to look.
  //
  // This is the exact defect the codebase already has a name for — `isUnmeasured`
  // in Index.tsx, and scoring.ts's own note that an empty roadClassMeters map is
  // not a measurement of zero. It was fixed in the engine and never in the copy.
  //
  // Deliberately NOT reusing scoring.ts's `hasRoadClassEvidence`: it demands
  // `motorwayMeters > 0`, which a genuinely calm route legitimately lacks, so it
  // answers "is there highway here" rather than "did anyone measure". The
  // question here is only the second one.
  const _hwyKnown = Number.isFinite(_ff?.highwayPct as number);
  const _majorKnown = Number.isFinite(_ff?.majorRoadPct as number);
  const _featuresMeasured = _hwyKnown && _majorKnown;
  // Second half of the item-1 fix: the Google carrier's metrics are finite
  // but describe ANOTHER route (or nothing) — refuse the label for them too.
  const fastIsPleasant = computeFastIsPleasant({
    featuresMeasured: _featuresMeasured,
    measuredOnThisGeometry: !isGoogle,
    nonEpicHighwayPct: _nonEpicHighwayPct,
    majorRoadPct: _ff?.majorRoadPct,
    calmScore: route.scores?.calm,
    pleasantScore: route.scores?.pleasant,
    throughPark: !!fastThroughPark,
    carFreeKm: _ff?.carFreeKm,
    residentialPct: _ff?.residentialPct,
    waterNamesCount: _ff?.waterNames?.length ?? 0,
    treeLinedKm: _ff?.treeLinedKm,
  });
  // THE LABEL'S CONFESSION (rule 17 + rule 19). `fastIsPleasant` is an AND of
  // three groups over seven values, any one of which silently kills the
  // "Fast & pleasant" title. Twice today I picked a likely culprit and shipped
  // it; both times the real value was somewhere I hadn't looked. This prints
  // every input so the next run names the branch instead of me guessing it.
  if (route.classification === "fastest") {
    // eslint-disable-next-line no-console
    console.log(
      `[label] fastIsPleasant=${fastIsPleasant} ` +
        `measured=${_featuresMeasured}${_featuresMeasured ? "" : " ⚠ FEATURES UNKNOWN — label refused"} ` +
        `src=${getRouteSource(route.route) ?? "unknown"} ` +
        `hwy=${Math.round(_ff?.highwayPct ?? 0)}% epic=${Math.round(_epicSharePct)}% ` +
        `→ nonEpicHwy=${Math.round(_nonEpicHighwayPct)}% (needs <30) ` +
        `major=${Math.round(_ff?.majorRoadPct ?? 0)}% (needs <50) ` +
        `| calm=${route.scores?.calm ?? "?"} pleasant=${route.scores?.pleasant ?? "?"} ` +
        `park=${fastThroughPark ?? "none"} carFreeKm=${(_ff?.carFreeKm ?? 0).toFixed(1)} ` +
        `resid=${Math.round(_ff?.residentialPct ?? 0)}% water=${_ff?.waterNames?.length ?? 0} ` +
        `trees=${(_ff?.treeLinedKm ?? 0).toFixed(1)}km ` +
        `|| isOnlyOption=${isOnlyOption === true} noMiddleOption=${noMiddleOption === true} ` +
        `epicM=${_epicM} routeM=${Math.round(_routeM)}`,
    );
  }
  // The specific positive detail for the "Fast & pleasant" copy. Prefer the
  // strongest true thing the route HAS: a park it runs through (on trails when
  // it's also on car-free paths — "through Rockefeller on walking trails"),
  // else just the park, else car-free trails, else calm local streets.
  const fastOnTrails = (_ff?.carFreeKm ?? 0) >= 0.4;
  const _words = travelWords(travelMode);
  const fastPleasantDetail = fastThroughPark
    ? fastOnTrails
      ? `through ${fastThroughPark} on ${_words.parkTrails}`
      : `through ${fastThroughPark}`
    : fastOnTrails
      ? `on ${_words.carFreeTrails}, away from traffic`
      : "calm, local streets";
  // (#155) Cheap-path Scenic: scenic anchors haven't loaded yet, so the
  // Scenic card is temporarily showing the same line as Pleasant/Direct.
  // Override the copy so the user knows we're still looking. Refreshes
  // automatically when enrichment finishes and the next classification
  // arrives (3-5 seconds typically).
  const isScenicSearching =
    route.scenicSearching === true && route.classification === "scenic";
  // Title follows the classifier. Exactly one card — the `fastest` one — gets
  // the (localized) fastest label via _brandLabel; pleasant/scenic keep theirs.
  // Use META.fastest.label (localized), never a hardcoded "Fast route" string.
  const _effectiveLabelRaw =
    isScenicSearching
      ? "Searching scenic…"
      // SAY BOTH (Morgan, Aug 9: "it just needs to say fastest and pleasant").
      // `isOnlyOption` was too narrow: on the Taconic run there IS a Scenic card,
      // so this was false, and the fastest-and-pleasant truth had nowhere to
      // appear. `noMiddleOption` means the Pleasant slot is empty BECAUSE no
      // calmer alternative was worth showing — which, when the fast route is
      // itself pleasant, is exactly the case this label exists for.
      : (isOnlyOption || noMiddleOption)
        ? (fastIsPleasant ? "Fast & pleasant" : META.fastest.label)
        : sameAsLabel
          ? sameAsLabel
          // QUALITY GATE, NOT JUST IDENTITY (card-copy audit, Aug 24).
          // `sameAsFastest` says the pleasant pick COLLAPSED ONTO the direct
          // route — an identity fact. It says nothing about whether that road
          // is pleasant. Titling on it alone is the same defect rule 20 was
          // written for, one layer up: the label claims a quality nobody
          // checked. `fastIsPleasant` is that check, and it already guards
          // the isOnlyOption/noMiddleOption branch three lines above; this
          // branch was simply missed. Without the gate a 100%-highway route
          // wears "Fast & Pleasant" purely because it happened to be both
          // slots' answer.
          : route.sameAsFastest && fastIsPleasant
            ? "Fast & Pleasant"
            : _brandLabel;
  // ONE GATE, EVERY SOURCE (card-copy audit, Aug 24).
  //
  // The word "pleasant" can reach this title from THREE places, and the first
  // fix only closed one of them:
  //   1. the `sameAsFastest` branch just above;
  //   2. `route.variantLabel`, handed in already reading "Fast & Pleasant"
  //      when the pleasant slot resolves to the fastest route — it arrives
  //      through fallbackBrandLabel → _brandLabel and was never checked;
  //   3. `pleasantBrandLabel`, which IS already guarded, by
  //      `recommendedIsHighwaySlog` ("a highway slog never wears the pleasant
  //      name", Morgan, Aug 2 — Portland → SF, nine hours of I-5 titled
  //      "Pleasant route").
  //
  // Route 2 is how SF → Santa Cruz still rendered "Fast & Pleasant" on a
  // 100%-highway route after fix 1: the label was decided upstream, so no
  // amount of guarding the local branch could catch it. Rather than chase
  // each producer, assert the invariant once where they converge — this card
  // may not call itself pleasant unless the quality gate says it is. Scoped
  // to the fastest card: a pleasant-classified card carrying a pleasant
  // label is the normal, correct case.
  const _effectiveLabel =
    route.classification === "fastest" &&
    /pleasant/i.test(_effectiveLabelRaw) &&
    !fastIsPleasant
      ? META.fastest.label
      : _effectiveLabelRaw;
  if (isScenicSearching) {
    meta = { ...meta, sub: "Looking for a beautiful detour…" };
  } else if (isOnlyOption || noMiddleOption) {
    meta = {
      ...meta,
      sub: fastIsPleasant
        ? `Fast and pleasant — ${fastPleasantDetail}`
        // NOT pleasant. Restored Aug 9 — 2ccc220 put the pleasant sentence on
        // this branch, which is the one that fires when the route ISN'T
        // pleasant (a walk that is 70% main roads told the driver the fastest
        // way was also the pleasant one). The shortening Morgan asked for
        // belongs to the real headline in routeReason.ts:337, and is applied
        // in Index.tsx:3017 for CarPlay. Here the honest line is the plain one.
        : "The most direct way here",
    };
  } else if (sameAsSub) {
    meta = { ...meta, sub: sameAsSub };
  } else if (route.sameAsFastest) {
    // Match the sub-label to the title so the card reads coherently.
    meta = { ...meta, sub: "Same path the fastest route would give" };
  }
  // When the recommended PLEASANT pick is also the fastest way (no real time
  // penalty over the quickest route), the title should say BOTH — "Fast &
  // pleasant" — not just "Pleasant route" (Morgan). The route still gets you
  // there quickest AND it's the nicer way.
  const pleasantIsAlsoFastest =
    route.classification === "pleasant" &&
    (recommended ?? true) &&
    !route.sameAsFastest &&
    fastestDuration != null &&
    route.route.duration <= fastestDuration + 45;
  // (Morgan) A card in the Pleasant slot that is actually a HIGHWAY route (the
  // I-95/I-287 case) must never read "Pleasant route" — just call it the Fast
  // route. Simple, reliable label override independent of the classifier: if
  // this pleasant card rides main roads/highways, title it like the Direct card.
  const pleasantIsReallyHighway =
    route.classification === "pleasant" && (route.features?.highwayPct ?? 0) >= 45;
  const displayLabel =
    optionNumber != null
      ? `Option ${optionNumber}`
      : pleasantIsReallyHighway
        ? META.fastest.label
        : pleasantIsAlsoFastest
          ? "Fast & pleasant"
          : _effectiveLabel;
  /** When true, this card shouldn't be marketed as the pleasant pick —
   *  used to suppress the "For you" badge below.
   *  (#135) Also fires for sameAsFastest cards: when Pleasant collapses
   *  onto the direct route, the card is just the fastest route under a
   *  pleasant slot, not a real pleasant pick. "For you" on it would lie. */
  // Scenic ("the long way") never wears the "For you" badge — that's the
  // Pleasant recommendation. It's offered as an option, titled "Scenic route".
  const isFakeWander =
    isOnlyOption === true || route.sameAsFastest === true || route.classification === "scenic";
  // Single source of truth for "+X min" deltas: always measure against the
  // SAME duration value the card actually displays for this route. When the
  // Direct card is showing Google's ETA via `googleOverride`, we treat
  // `displayDuration` as the route's own duration too — otherwise the
  // baseline pill could show a non-zero "+1 min vs itself" because Mapbox
  // and Google disagree by a few seconds, and the explanation factors below
  // would inherit that contradiction.
  const ownDuration = useGoogleBaseline ? displayDuration : route.route.duration;
  const extraSec = fastestDuration != null ? Math.max(0, ownDuration - fastestDuration) : 0;
  const extraMin = Math.round(extraSec / 60);
  // "% more pleasant than the fastest route". For any route we surface as
  // an alternative, we guarantee a meaningful floor (20%) so the bar never
  // looks empty, and we bump it by +5% per applied "Drive by..." detour
  // since each detour intentionally trades time for pleasantness.
  const thisPleasant = Math.max(0, route.scores.pleasant ?? 0);
  const baseline = Math.max(1, fastestPleasantScore ?? thisPleasant);
  const isBaseline = route.classification === "fastest";
  const rawPct = Math.round(((thisPleasant - baseline) / baseline) * 100);
  const floor = 20 + Math.max(0, detourCount) * 5;
  const improvementPct = isBaseline ? 0 : Math.max(floor, rawPct);
  // Bar fill: scale the improvement so 0% = empty, 50%+ = full.
  const barPct = Math.max(3, Math.min(100, improvementPct * 2));
  // Describe the route by what's actually on it, not the user's profile
  // setting. A "Nature-heavy" profile pick on an entirely-urban route
  // should still read as "City streets" / "Historic" / etc.
  const f = route.features;
  const km = Math.max(0.1, route.route.distance / 1000);
  const parkRich = (f.parks ?? 0) >= 3 || (f.parkNames?.length ?? 0) >= 2;
  const waterRich = (f.waterPct ?? 0) >= 8 || (f.waterNames?.length ?? 0) >= 1;
  const historicRich = (f.historicNames?.length ?? 0) >= 2 || (f.landmarkNames?.length ?? 0) >= 3;
  const livelyRich = ((f.cafes ?? 0) + (f.shops ?? 0)) / km >= 4;
  const residentialRich = (f.residentialPct ?? 0) >= 55;
  let routeTone: string;
  if (waterRich && parkRich) routeTone = "Parks & water";
  else if (waterRich) routeTone = "Waterfront";
  else if (parkRich) routeTone = "Park-rich";
  else if (historicRich) routeTone = "Historic streets";
  else if (livelyRich) routeTone = "Lively streets";
  else if (residentialRich) routeTone = "Quiet side streets";
  else routeTone = "City streets";
  const profileLabel = routeTone;
  const chips = route.evidence ?? [];

  // Build a factual "why this route" line from measured route attributes.
  // Each bit is a concrete number, not a vibe.
  // Aug 19 units audit: was an inline miles/feet formatter. fmtDistance
  // honours the driver's chosen system so these factor lines can't
  // contradict the distances everywhere else on the card.
  const fmtMi = (m: number) => fmtDistance(m);
  // Structured "why this route" factors. Each factor has:
  //   text       — short human-readable phrase
  //   kind       — 'positive' (good thing), 'neutral' (factual), 'caveat' (downside)
  //   confidence — 'high'   = derived from OSM geometry / direct measurement
  //                'medium' = derived from sampled corridor stats
  //                'low'    = heuristic / label-based fallback
  // Coastal/waterside positives are pushed first when they apply so they
  // remain the lead reason in the UI.
  type WhyTopic =
    | "iconic"    // Redwoods, Grand Canyon, Mount Washington, etc. — always leads
    | "coastal"   // along ocean / river / lake / canal
    | "parks"     // park & nature POIs near route
    | "anchor"    // specific named landmark / historic site
    | "calm"      // residential / quiet streets
    | "busy"      // busy / arterial roads
    | "speed"     // duration / distance fact
    | "none";
  type WhyFactor = {
    text: string;
    kind: "positive" | "neutral" | "caveat";
    confidence: "high" | "medium" | "low";
    topic: WhyTopic;
    /** When topic === "anchor", the POI name to look up in poiSegments. */
    anchorName?: string;
  };
  const factors: WhyFactor[] = [];
  const pushFactor = (f: WhyFactor) => factors.push(f);

  // 0) COASTAL / WATERSIDE HIGHLIGHT — when the route runs a meaningful
  //    stretch along an ocean, lake, river, or canal, that's THE reason
  //    to take this route. Surface it first.
  //
  //    Detection priority (geometry FIRST, labels as a tiebreaker):
  //      a) `coastlineAdjacentMeters` from OSM `kind=coastline` —
  //         truthful "ocean-side" signal, independent of road shields.
  //      b) `waterAdjacency` (per-feature meters) — names the most
  //         prominent body of water the route runs along.
  //      c) `waterAdjacentMeters` total — generic riverside/lakefront fallback.
  //      d) Coastal highway label (PCH/CA-1/A1A/…) — only used to BOOST the
  //         confidence/wording when geometry already says we're waterside,
  //         not as the sole trigger.
  const COASTAL_HWY_RE = /\b(CA-?1|US-?1|A1A|HI-?360|FL-?A1A|PCH|Pacific Coast|Highway\s*1|Hwy\s*1|Overseas Hwy|Skyline)\b/i;
  const epicMeters = route.route.epicHighwayMeters ?? 0;
  const epicLabels = route.route.epicHighwayLabels ?? [];
  const coastalHwyLabel = epicLabels.find((l) => COASTAL_HWY_RE.test(l));
  const adj = f.waterAdjacency ?? [];
  const adjLead = adj[0];
  const coastM = f.coastlineAdjacentMeters ?? 0;
  // Geometry-derived adjacent meters; fall back to the old waterPct estimate
  // only when OSM water lines/points weren't available for this corridor.
  const waterAdjMetersGeo = f.waterAdjacentMeters ?? 0;
  const waterAdjMetersFallback = (route.route.distance * (f.waterPct ?? 0)) / 100;
  const waterAdjMeters = waterAdjMetersGeo > 0 ? waterAdjMetersGeo : waterAdjMetersFallback;
  const waterAdjacentMi = waterAdjMeters / 1609.34;
  // Aug 19 units audit: used to return a bare number with " mi" pasted on at
  // every call site. Now returns the FULL fmtDistance string (value + unit)
  // so the unit follows the driver's chosen system; callers no longer append
  // their own. Still takes miles because the surrounding math is in miles.
  const fmtAdjMi = (mi: number) => fmtDistance(mi * 1609.344);
  const coastMi = coastM / 1609.34;


  // -1) ICONIC LANDMARKS — Redwoods, Grand Canyon, Mount Washington, etc.
  //     These are once-in-a-trip moments and ALWAYS deserve to be the lead
  //     reason on the card. Detected via curated bbox + name regex (see
  //     `iconicLandmarks.ts`), so we catch them even when OSM corridor
  //     tagging is sparse on huge multi-polygons.
  const iconicMatches = findIconicLandmarks(
    route.route.coordinates,
    [
      ...(f.parkNames ?? []),
      ...(f.natureNames ?? []),
      ...(f.landmarkNames ?? []),
      ...(f.historicNames ?? []),
      ...(f.waterNames ?? []),
    ],
    2,
  );
  for (const icon of iconicMatches) {
    const prep = icon.preposition ?? "by";
    pushFactor({
      text: `${prep} ${icon.name}`,
      kind: "positive",
      confidence: "high",
      topic: "iconic",
      anchorName: icon.name,
    });
  }

  // a) Genuine OCEAN coastline — strongest signal (geometry-derived).
  if (coastM >= 400) {
    const distStr = fmtAdjMi(coastMi);
    const oceanName = adj.find((a) => a.kind === "coastline" && a.name)?.name;
    const along = coastalHwyLabel ? `along ${coastalHwyLabel}` : "along the coast";
    pushFactor({
      text: oceanName ? `${distStr} ${along} by ${oceanName}` : `${distStr} ${along}`,
      kind: "positive",
      confidence: "high",
      topic: "coastal",
    });
  }
  // b) Named river/canal/lake adjacency from OSM geometry.
  else if (adjLead && adjLead.meters >= 500 && adjLead.name) {
    const distStr = fmtAdjMi(adjLead.meters / 1609.34);
    const verb = adjLead.kind === "river" || adjLead.kind === "canal" ? "along" : "by";
    pushFactor({ text: `${distStr} ${verb} ${adjLead.name}`, kind: "positive", confidence: "high", topic: "coastal" });
  }
  // c) Generic waterside stretch from geometry — no name available.
  else if (waterAdjMetersGeo >= 500) {
    pushFactor({ text: `${fmtAdjMi(waterAdjacentMi)} along water`, kind: "positive", confidence: "high", topic: "coastal" });
  }
  // d) Last-resort fallback to the count-based waterPct heuristic.
  else if (f.waterAdjacentMeters == null && coastalHwyLabel && epicMeters > 1000) {
    const mi = epicMeters / 1609.34;
    const distStr = fmtAdjMi(mi);
    const leadWater = f.waterNames?.[0];
    pushFactor({
      text: leadWater
        ? `along ${coastalHwyLabel} by ${leadWater} (${distStr})`
        : `${distStr} along ${coastalHwyLabel} by the coast`,
      kind: "positive",
      confidence: "low",
      topic: "coastal",
    });
  } else if (f.waterAdjacentMeters == null && (f.waterPct ?? 0) >= 12 && waterAdjacentMi >= 0.5) {
    const distStr = fmtAdjMi(waterAdjacentMi);
    const leadWater = f.waterNames?.[0];
    pushFactor({
      text: leadWater ? `${distStr} along ${leadWater}` : `${distStr} along the water`,
      kind: "positive",
      confidence: "low",
      topic: "coastal",
    });
  }

  // 1) Time/distance delta vs the Direct route.
  if (isBaseline) {
    pushFactor({ text: `fastest path · ${fmtMi(displayDistance)}`, kind: "neutral", confidence: "high", topic: "speed" });
  } else if (extraMin > 0) {
    // Mirror the pill + ETA-confidence chip rendered at the top of the
    // card 1:1 — same `+X min` text, same `≈` prefix when the delta falls
    // inside traffic noise (or is otherwise approximate). Reusing
    // `extraMin` and `etaConfidence()` here guarantees the factor copy
    // can never claim a different timing story than the badges sitting a
    // few pixels above it.
    const conf = etaConfidence(extraMin, fastestDuration);
    const approx = conf !== "confident";
    pushFactor({
      text: `${approx ? "≈ " : ""}+${extraMin} min vs fastest`,
      // Approximate / noisy deltas are demoted to "neutral" so the chip
      // doesn't read as a hard caveat when the badge itself is hedging.
      kind: !approx && extraMin >= 10 ? "caveat" : "neutral",
      confidence: approx ? "low" : "high",
      topic: "speed",
    });
  }

  // 2) Busy-roads delta vs Direct.
  if (!isBaseline && fastestFeatures) {
    const baseHwy = fastestFeatures.highwayPct ?? 0;
    const thisHwy = f.highwayPct ?? 0;
    if (baseHwy >= 5) {
      const delta = Math.round(((thisHwy - baseHwy) / baseHwy) * 100);
      if (delta <= -10)
        pushFactor({ text: `${delta}% time on busy roads`, kind: "positive", confidence: "medium", topic: "busy" });
      else if (delta >= 15)
        pushFactor({ text: `+${delta}% time on busy roads`, kind: "caveat", confidence: "medium", topic: "busy" });
    } else if (thisHwy >= 15) {
      pushFactor({ text: `${Math.round(thisHwy)}% on busy roads`, kind: "caveat", confidence: "medium", topic: "busy" });
    }
  } else if (isBaseline) {
    const baselineHwy = useGoogleBaseline ? googleOverride!.highwayPct : (f.highwayPct ?? 0);
    if (baselineHwy >= 20) {
      pushFactor({ text: `${Math.round(baselineHwy)}% on busy roads`, kind: "neutral", confidence: useGoogleBaseline ? "high" : "medium", topic: "busy" });
    }
  }

  // 3) Parks count near the route.
  const parkCount = f.parks ?? 0;
  if (parkCount >= 1) {
    const lead = f.parkNames?.[0];
    if (parkCount >= 2) {
      pushFactor({
        text: `passes ${parkCount} parks within 0.1 mi${lead ? ` (incl. ${lead})` : ""}`,
        kind: "positive",
        confidence: "high",
        topic: "parks",
      });
    } else if (lead) {
      pushFactor({ text: `passes ${lead}`, kind: "positive", confidence: "high", topic: "anchor", anchorName: lead });
    } else {
      pushFactor({ text: `passes 1 park within 0.1 mi`, kind: "positive", confidence: "medium", topic: "parks" });
    }
  }

  // 4) Waterfront fallback when (0) didn't fire and no geometry data.
  const alreadyHasCoastal =
    coastM >= 400 ||
    (adjLead && adjLead.meters >= 500) ||
    waterAdjMetersGeo >= 500 ||
    !!coastalHwyLabel ||
    ((f.waterPct ?? 0) >= 12 && waterAdjacentMi >= 0.5);
  if (!alreadyHasCoastal && f.waterAdjacentMeters == null && (f.waterPct ?? 0) >= 6) {
    pushFactor({
      text: `${fmtMi((route.route.distance * (f.waterPct ?? 0)) / 100)} along water`,
      kind: "positive",
      confidence: "low",
      topic: "coastal",
    });
  }

  // 5) Quiet residential streets.
  const onBigRoads = (f.highwayPct ?? 0) >= 25 || (f.majorRoadPct ?? 0) >= 35;
  if (!onBigRoads && (f.residentialPct ?? 0) >= 55 && factors.length < 4) {
    pushFactor({
      text: `${Math.round(f.residentialPct)}% on quiet streets`,
      kind: "positive",
      confidence: "medium",
      topic: "calm",
    });
  }

  // 6) Named historic / landmark anchor.
  const anchor = f.historicNames?.[0] ?? f.landmarkNames?.[0];
  if (anchor && factors.length < 4) {
    pushFactor({ text: `passes ${anchor}`, kind: "positive", confidence: "high", topic: "anchor", anchorName: anchor });
  }

  if (factors.length === 0) {
    pushFactor({
      text: `${fmtMi(route.route.distance)} · ${Math.round(route.route.duration / 60)} min`,
      kind: "neutral",
      confidence: "high",
      topic: "speed",
    });
  }

  // Coastal/waterside positive — when present at index 0 — stays anchored
  // as the lead factor. Remaining factors are ranked by kind then confidence.
  // Anchor order: iconic landmarks ALWAYS lead (Redwoods, Grand Canyon,
  // Mt. Washington, …). Coastal/waterside positives stay anchored next.
  // Remaining factors are ranked by kind then confidence.
  const iconicHead = factors.filter((x) => x.topic === "iconic").slice(0, 2);
  const remaining = factors.filter((x) => x.topic !== "iconic");
  const leadIsCoastal =
    remaining[0]?.topic === "coastal" && remaining[0]?.kind === "positive";
  const kindRank: Record<WhyFactor["kind"], number> = { positive: 0, neutral: 1, caveat: 2 };
  const confRank: Record<WhyFactor["confidence"], number> = { high: 0, medium: 1, low: 2 };
  const coastalHead = leadIsCoastal ? remaining.slice(0, 1) : [];
  const tail = (leadIsCoastal ? remaining.slice(1) : remaining).slice().sort((a, b) => {
    const k = kindRank[a.kind] - kindRank[b.kind];
    if (k !== 0) return k;
    return confRank[a.confidence] - confRank[b.confidence];
  });
  const topFactors: WhyFactor[] = [...iconicHead, ...coastalHead, ...tail].slice(0, 3);

  // -------------------------------------------------------------------
  // Build a map-highlight payload for a "why this route" factor so users
  // can click the explanation and see exactly which polyline stretch +
  // which nearby POIs supported that scoring signal.
  //
  // Strategy:
  //   - Filter `f.poiSegments` (already snapped to the route polyline by
  //     `mapPoisToSegments`) by the factor's topic.
  //   - Compute the contiguous segment range covering those snaps and
  //     pass a padded slice as `coords`.
  //   - Pass the matching POIs so the map can pulse cafes / parks / water
  //     features alongside the polyline highlight.
  // Returns null when there's nothing meaningful to show (e.g. the
  // "fastest path" / "+5 min" speed factor isn't a stretch on the map).
  // -------------------------------------------------------------------
  const buildFactorHighlight = (fac: WhyFactor): MapHighlightPayload | null => {
    const coords = route.route.coordinates;
    if (!coords || coords.length < 2) return null;
    const snaps: PoiSegmentMatch[] = f.poiSegments ?? [];
    let picks: PoiSegmentMatch[] = [];

    if ((fac.topic === "anchor" || fac.topic === "iconic") && fac.anchorName) {
      // Iconic landmarks (Redwoods, Grand Canyon, …) often won't have a
      // POI snap because the underlying OSM polygon is huge / unnamed in
      // the corridor scan. We try a snap match first, then fall back to
      // flashing the whole route polyline so the user still gets visual
      // confirmation of which leg crosses the icon.
      const target = fac.anchorName.toLowerCase();
      const cleaned = target.replace(/^(the |mount |mt\.?\s*)/i, "");
      picks = snaps.filter(
        (m) => m.name.toLowerCase() === target || m.name.toLowerCase().includes(cleaned),
      );
      if (picks.length === 0 && fac.topic === "iconic") {
        return { coords, pois: [], label: fac.text, ttlMs: 5000, fit: false };
      }
    } else if (fac.topic === "coastal") {
      picks = snaps.filter((m) => m.kind === "water");
      // Enrich with nearby cafes/landmarks within 600m of the water snaps
      // so the user sees what's actually *on* this stretch.
      if (picks.length > 0) {
        const minSeg = Math.min(...picks.map((m) => m.segmentIndex));
        const maxSeg = Math.max(...picks.map((m) => m.segmentIndex));
        const nearby = snaps.filter(
          (m) =>
            m.segmentIndex >= minSeg - 6 &&
            m.segmentIndex <= maxSeg + 6 &&
            (m.kind === "cafe" || m.kind === "landmark" || m.kind === "viewpoint" || m.kind === "historic"),
        );
        picks = [...picks, ...nearby];
      }
    } else if (fac.topic === "parks") {
      picks = snaps.filter((m) => m.kind === "park" || m.kind === "nature");
    } else if (fac.topic === "calm" || fac.topic === "busy") {
      // No POI anchors — highlight the entire route polyline so users see
      // the road context at a glance, with no fit (camera stays put).
      return {
        coords,
        pois: [],
        label: fac.text,
        ttlMs: 4500,
        fit: false,
      };
    } else {
      return null;
    }

    if (picks.length === 0) return null;
    const minIdx = Math.min(...picks.map((m) => m.segmentIndex));
    const maxIdx = Math.max(...picks.map((m) => m.segmentIndex));
    const pad = 6;
    const s = Math.max(0, minIdx - pad);
    const e = Math.min(coords.length - 1, maxIdx + 1 + pad);
    return {
      coords: coords.slice(s, e + 1),
      pois: picks.map((m) => ({ point: m.point, name: m.name })),
      label: fac.text,
      ttlMs: 6000,
    };
  };

  const factorHighlights = topFactors.map(buildFactorHighlight);


  const fireSelectAnalytics = () => {
    // WHERE. These columns have existed since the table was created and were
    // never once filled — 4,466 rows, all NULL, so two months of routing
    // history could not say which continent it happened on.
    const { start, end } = endpointsOf(
      route.route?.coordinates as [number, number][] | undefined,
    );
    logRouteInteraction({
      action: "route_select",
      start,
      end,
      travelMode: (route as unknown as { route?: { profile?: string } }).route?.profile ?? null,
      routeIndex: typeof optionNumber === "number" ? optionNumber - 1 : null,
      routePleasantScore: route.scores?.pleasant ?? null,
      routeDistanceM: route.route?.distance ?? null,
      routeDurationS: route.route?.duration ?? null,
      isRecommended: !!recommended,
    });
  };

  return (
    <div
      role={whyOpen ? undefined : "button"}
      tabIndex={whyOpen ? -1 : 0}
      onClick={whyOpen ? undefined : () => { fireSelectAnalytics(); onClick(); }}
      onKeyDown={(e) => {
        if (whyOpen) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          fireSelectAnalytics();
          onClick();
        }
      }}
      className={`card-press relative w-full h-full flex flex-col text-left ${whyOpen ? "p-0" : (selected ? "p-3.5" : "p-2.5")} rounded-3xl transition-shadow duration-500 ${
        /* (#163 → inline-styled) Tailwind purging was removing bg-sky-50
         *  and ring-sky-500 classes because they weren't used elsewhere,
         *  so non-Pleasant selected cards stayed invisible. Switching to
         *  inline styles bypasses the purge — selected non-Pleasant cards
         *  now reliably get a bold blue ring + light blue background. */
        selected
          ? isPleasant
            ? "shadow-elevated"
            : "shadow-elevated"
          : isPleasant
            ? "bg-mint-soft/95 border border-primary/30 shadow-soft"
            : "bg-card border border-border hover:bg-muted/40 hover:shadow-soft"
      } ${whyOpen ? "cursor-default" : "cursor-pointer"} ${
        scoreFlash
          ? scoreFlash.delta > 0
            ? "ring-2 ring-pleasant/70 shadow-elevated"
            : "ring-2 ring-destructive/60 shadow-elevated"
          : ""
      }`}
      style={selected ? {
        backgroundColor: isPleasant ? "rgb(230, 246, 240)" : "rgb(224, 242, 254)",
        border: "2px solid rgb(14, 165, 233)",
        boxShadow: "0 0 0 2px rgb(14, 165, 233), 0 4px 12px rgba(0,0,0,0.08)",
      } : undefined}
    >
      {/* Transient delta chip — appears briefly when this route's pleasant
       *  score shifts (e.g. after a mid-trip toggle rating triggers a rescore). */}
      {scoreFlash && !whyOpen && (
        <div
          key={scoreFlash.key}
          className={`pointer-events-none absolute -top-2 right-3 z-10 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold shadow-soft animate-fade-in ${
            scoreFlash.delta > 0
              ? "bg-pleasant text-primary-foreground"
              : "bg-destructive text-destructive-foreground"
          }`}
          aria-live="polite"
        >
          {scoreFlash.delta > 0 ? (
            <TrendingUp className="w-3 h-3" />
          ) : (
            <TrendingDown className="w-3 h-3" />
          )}
          {scoreFlash.delta > 0 ? `+${scoreFlash.delta}` : scoreFlash.delta} pleasant
        </div>
      )}
      {whyOpen && whyContent ? (
        <div className="overflow-hidden rounded-3xl">{whyContent}</div>
      ) : !selected ? (
        <div className="flex items-start gap-3">
          <div
            className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
              isPleasant ? "bg-pleasant/10 text-primary" : "bg-muted text-foreground/80"
            }`}
            aria-hidden
          >
            <meta.Icon className="w-4.5 h-4.5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <span className="heading text-sm font-semibold truncate">
                {/* `&& fastIsPleasant` — audit Aug 24. This ternary bypasses
                  * _effectiveLabel entirely, so the quality gate applied there
                  * never reached the rendered title. SF → Santa Cruz and
                  * Asheville → Hendersonville both shipped "Fastest & Pleasant"
                  * on routes the same render had just logged as
                  * `fastIsPleasant=false hwy=100%`. */}
                {optionNumber != null
                  ? displayLabel
                  : isOnlyOption
                    ? _effectiveLabel
                    : route.classification === "fastest" && route.sameAsFastest && fastIsPleasant
                      ? "Fastest & Pleasant"
                      : _effectiveLabel}
              </span>
              {isPleasant && !isFakeWander && (
                <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-primary/15 text-primary font-semibold flex-shrink-0">
                  For you
                </span>
              )}
            </div>
            {/* Clean one-sentence reason on the collapsed card too, so every
             *  card reads the same way: title → why → time. */}
            {reason && (
              <p className="text-[13px] leading-snug text-foreground mt-0.5">{reason.headline}</p>
            )}
            {reason?.comparison && (
              <p className="text-[12px] leading-snug text-muted-foreground mt-0.5">{reason.comparison}</p>
            )}
            {/* Combined meta + badge row. Time + distance stay; the old chip
             *  cloud is hidden (the reason line above carries the story). */}
            <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="flex items-center gap-1 flex-shrink-0">
                <Clock className="w-3 h-3" /> {fmtMin(displayDuration)}
              </span>
              <span className="flex items-center gap-1 flex-shrink-0">
                <RouteIcon className="w-3 h-3" /> {fmtKm(displayDistance)}
              </span>
              {/* THE DESIGNED SLOT for the time trade (audit, Aug 24). The
                * comment below this row says "the one-sentence reason + time
                * trade above carries the comparison" — but `reason.tradeoff`
                * is disabled in routeReason.ts (its baseline is unreliable),
                * so this slot has been rendering nothing and the `+N min`
                * pills below are inside `<div className="hidden">`. Net
                * effect on the audited cards: Phoenix → Prescott showed
                * +1h 51m of extra driving with no time cost visible anywhere.
                * `timeTradeLine` fills the slot from the on-screen baseline. */}
              {(timeTradeLine || reason?.tradeoff) && (
                <span className="min-w-0 break-words">· {timeTradeLine || reason!.tradeoff}</span>
              )}
              <div className="hidden">
                {extraMin > 0 && !useGoogleBaseline && (
                  <span
                    className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-muted text-foreground/70"
                    title={`${extraMin} min longer than the fastest option`}
                  >
                    +{extraMin} min
                  </span>
                )}
                {extraMin > 0 && !useGoogleBaseline && (
                  <EtaConfidenceChip extraMin={extraMin} fastestDurationS={fastestDuration} size="xs" />
                )}
                {extraMin > 0 && baselineMode && baselineOptions && onBaselineModeChange && (
                  <BaselineToggle mode={baselineMode} options={baselineOptions} onChange={onBaselineModeChange} size="xs" />
                )}
                {shouldShowHighwayBadge(googleHighwayDeltaPP) && (
                  <HighwayBadge deltaPP={googleHighwayDeltaPP} size="xs" thisHighwayPct={thisHighwayPct} googleHighwayPct={googleHighwayPct} routeName={typeof displayLabel === "string" ? displayLabel : (route.variantLabel ?? meta.label)} />
                )}
                {/* WALKSCORE_DEBUG_PILL — verification chip (DISABLED #148).
                 *  Morgan: "i don't want to see a walk score". The pill was
                 *  meant to be a tester-verification chip (see #102) but
                 *  ended up rendering on driving cards where it adds noise.
                 *  Keeping the code in place so we can re-enable for walk-
                 *  mode-only testing later — flip the `false` to `true`. */}
                {/* eslint-disable-next-line no-constant-binary-expression --
                    deliberate kill switch (#148, "i don't want to see a walk
                    score"), kept per the comment above so walk-mode testing
                    can flip it back on. Not a dead guard. */}
                {false && (() => {
                  const key = (import.meta.env as Record<string, string | undefined>).VITE_WALK_SCORE_KEY;
                  if (!key || key.length === 0) return null;
                  const score = route.features.walkScore;
                  if (typeof score === "number") {
                    return (
                      <span
                        className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200"
                        title={`Walk Score: ${Math.round(score)} / 100`}
                      >
                        Walk Score {Math.round(score)}
                      </span>
                    );
                  }
                  return (
                    <span
                      className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200"
                      title="Walk Score key configured, but no score returned for this route. Lookup may have failed or no coverage."
                    >
                      Walk Score —
                    </span>
                  );
                })()}
                {/* Community "loved by locals" chip (#124). When the
                 *  route's community signal is significantly positive
                 *  (well above neutral 50), surface as social proof.
                 *  Hidden when community is neutral / negative so the
                 *  chip only appears when there's real positive lift. */}
                {(route.scores?.community ?? 50) >= 65 && (
                  <span
                    className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-pink-100 text-pink-700 border border-pink-200"
                    title={`Community pleasantness score: ${Math.round(route.scores.community)}/100 — locals have rated this stretch positively.`}
                  >
                    Loved by locals
                  </span>
                )}
                {/* Absolute "X% highway" chip — always visible whenever the
                 *  route has non-trivial highway exposure (≥10%), so every
                 *  card surfaces the same comparable data point. The
                 *  HighwayBadge above only fires when this route uses LESS
                 *  highway than Google (positive-story rule), which leaves
                 *  the Fastest card with no highway info at all. This chip
                 *  fills that gap — neutral tone when light (<30%), caveat
                 *  tone when heavy (≥60%) so the user can spot busy routes
                 *  at a glance without expanding the card.
                 *  Real-world feedback: "the route cards should all have
                 *  available info to compare." */}
                {Number.isFinite(thisHighwayPct ?? NaN) && (thisHighwayPct as number) >= 10 && (
                  <span
                    className={`text-[11px] px-2 py-0.5 rounded-full tabular-nums ${
                      (thisHighwayPct as number) >= 60
                        ? "bg-amber-500/15 text-amber-700 dark:text-amber-400"
                        : "bg-muted text-foreground/70"
                    }`}
                    title={`${Math.round(thisHighwayPct as number)}% of this route runs on main roads`}
                  >
                    {Math.round(thisHighwayPct as number)}% main roads
                  </span>
                )}
                {/* Pleasantness chip — shown on every card so routes stay
                 *  comparable at a glance (real-world feedback: "we should
                 *  have pleasant rating on all the fastest routes as well").
                 *  Presented as an honest TIER WORD, not a raw number:
                 *  "Pleasant 28/100" and "Pleasant 0" confused three test
                 *  drives by implying a precision the model hasn't earned
                 *  (score calibration is pending real-drive data). The
                 *  numbers still live in the expanded ScoreBreakdown for
                 *  anyone who taps. When the score is built from no
                 *  evidence (cheap path / enrichment found nothing), say
                 *  "Limited data" instead of guessing. */}
                {Number.isFinite(route.scores?.pleasant) && (
                  isLimitedData(route.features) ? (
                    <span
                      className="text-[11px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium"
                      title="Not enough map data along this route yet to judge how pleasant it is."
                    >
                      Limited data
                    </span>
                  ) : (
                    <span
                      className={`text-[11px] px-2 py-0.5 rounded-full ${tierForScore(route.scores.pleasant).chipClass}`}
                      title="How pleasant this route is overall. Tap the card for the full breakdown."
                    >
                      {tierForScore(route.scores.pleasant).label}
                    </span>
                  )
                )}
                {!useGoogleBaseline && googleDurationS != null && (
                  <GoogleEtaBadge thisDurationS={route.route.duration} googleDurationS={googleDurationS} thisDistanceM={route.route.distance} googleDistanceM={googleDistanceM} size="xs" />
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
      <div className="flex items-start gap-3 sm:gap-4">
        <div
          className={`w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${
            isPleasant ? "bg-pleasant/10 text-primary" : "bg-muted text-foreground/80"
          }`}
          aria-hidden
        >
          <meta.Icon className="w-5 h-5 sm:w-6 sm:h-6" />
        </div>

        <div className="flex-1 min-w-0">
          {/* Always stack title row and meta row vertically — previously the
           *  `sm:flex-row sm:justify-between` made the title group and the
           *  meta badges (+X min, GoogleEtaBadge, BaselineToggle, etc.)
           *  share one horizontal row at small+ breakpoints. When the meta
           *  group's content was wide, the two would overlap and render text
           *  on top of the title. Real-world feedback: "this is also not
           *  working" on the Adventure card. Stacking gives every badge
           *  room and never overlaps. */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-wrap">
              <span className="heading text-lg sm:text-xl font-semibold break-words">
                {/* `&& fastIsPleasant` — audit Aug 24. Expanded-card twin of
                  * the collapsed-card title above; same bypass, same fix. */}
                {optionNumber != null
                  ? displayLabel
                  : isOnlyOption
                    ? _effectiveLabel
                    : route.classification === "fastest" && route.sameAsFastest && fastIsPleasant
                      ? "Fastest & Pleasant"
                      : _effectiveLabel}
              </span>
              {isPleasant && !isFakeWander && (
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/15 text-primary font-semibold">
                  For you
                </span>
              )}
              {/* "Same as direct route" badge — shown for any route flagged
               *  sameAsFastest, not just the "fastest" classification card.
               *  Real-world ride note: when Wander is literally the same
               *  road as Direct, the card should say so right in the title
               *  rather than showing two redundant cards.
               *
               *  Suppressed when isOnlyOption is set — the card title is
               *  already "Direct route" with a "No calmer route on this
               *  trip" sub, so a redundant badge would be noise. */}
              {route.sameAsFastest && !isOnlyOption && (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-pleasant/10 text-primary">
                  <Sparkles className="w-3 h-3" />
                  {route.classification === "fastest"
                    ? getRouteCopy().sameAsFastest
                    : "Same as direct route"}
                </span>
              )}
            </div>
            {/* THE ONE-SENTENCE WHY + time trade — the card's hero line.
             *  Clean, comparative, never fabricated (utils/routeReason.ts).
             *  Everything else lives behind "See details" below. */}
            <div className="mt-0.5">
              {reason && (
                <p className="text-[15px] leading-snug text-foreground">{reason.headline}</p>
              )}
              {reason?.comparison && (
                <p className="text-[13px] leading-snug text-muted-foreground mt-0.5">{reason.comparison}</p>
              )}
              {/* Time + distance + trade — ALWAYS shown (even on the Fastest /
               *  Direct card, which has no generated reason sentence), so every
               *  selected card surfaces its ETA. */}
              <div className="mt-1 text-[13px] text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="flex items-center gap-1 flex-shrink-0">
                  <Clock className="w-3.5 h-3.5" /> {fmtMin(displayDuration)}
                </span>
                <span className="flex items-center gap-1 flex-shrink-0">
                  <RouteIcon className="w-3.5 h-3.5" /> {fmtKm(displayDistance)}
                </span>
                {(timeTradeLine || reason?.tradeoff) && (
                  // Own flex item (not shrink-0) so a long tradeoff like "The
                  // long way — about 51 min more, but worth it" WRAPS instead of
                  // running off the card.
                  // Expanded-card twin of the collapsed slot. The first pass
                  // only filled the collapsed one, so the SELECTED card — the
                  // one the driver is actually reading — still showed no time
                  // cost. Caught on the SF re-run.
                  <span className="min-w-0 break-words">· {timeTradeLine || reason!.tradeoff}</span>
                )}
              </div>
              {/* Long trips: the ETA is an average-traffic estimate (we can't
               *  see congestion hundreds of miles out). Say so, and that it
               *  sharpens as you drive. */}
              {displayDistance >= LONG_TRIP_AVG_TRAFFIC_M && (
                <p className="mt-0.5 text-[12px] leading-snug text-muted-foreground">
                  Average traffic estimate — we'll refine it as you drive, up to 20 miles ahead.
                </p>
              )}
              {/* Curated 'great road' blurb — the specific, true local detail.
               *  Shown truncated at ~the first half with a More/Less toggle so
               *  a long descriptor never dominates the card. */}
              {reason?.featured?.blurb && (() => {
                const full = reason.featured.blurb;
                let head = full;
                let tail = "";
                if (full.length > 60) {
                  let cut = full.lastIndexOf(" ", Math.ceil(full.length / 2));
                  if (cut < 20) cut = full.indexOf(" ", Math.ceil(full.length / 2));
                  if (cut > 0) {
                    head = full.slice(0, cut).replace(/[\s,;—-]+$/, "");
                    tail = full.slice(cut);
                  }
                }
                return (
                  <p className="mt-1.5 text-[13px] leading-snug text-muted-foreground">
                    {blurbOpen || !tail ? full : <>{head}… </>}
                    {tail && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setBlurbOpen((v) => !v);
                        }}
                        className="ml-1 text-primary font-medium hover:underline"
                      >
                        {blurbOpen ? "Less" : "More"}
                      </button>
                    )}
                  </p>
                );
              })()}
              {/* "and N more pleasant roads" → expandable to the full list. */}
              {reason?.featured?.roads && reason.featured.roads.length > 2 && (
                <div className="mt-1.5 text-[13px]">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setRoadsOpen((v) => !v);
                    }}
                    className="text-primary font-medium hover:underline"
                  >
                    {roadsOpen
                      ? "Hide pleasant roads"
                      : `See all ${reason.featured.roads.length} pleasant roads`}
                  </button>
                  {roadsOpen && (
                    <ul className="mt-1 space-y-0.5 text-muted-foreground">
                      {reason.featured.roads.map((r) => (
                        <li key={r} className="flex items-center gap-1.5">
                          <span className="text-primary">·</span> {r}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
            {/* Pill row hidden from the card face per Morgan's redesign —
             *  the one-sentence reason + time trade above carries the
             *  comparison; the full numbers live behind "See details". */}
            <div className="hidden">
              {extraMin > 0 && !useGoogleBaseline ? (
                <span
                  className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                    isPleasant ? "bg-primary/10 text-primary" : "bg-muted text-foreground/70"
                  }`}
                  title={`${extraMin} min longer than the fastest option`}
                >
                  +{extraMin} min
                </span>
              ) : null}
              {shouldShowHighwayBadge(googleHighwayDeltaPP) ? (
                <HighwayBadge deltaPP={googleHighwayDeltaPP} size="sm" thisHighwayPct={thisHighwayPct} googleHighwayPct={googleHighwayPct} routeName={typeof displayLabel === "string" ? displayLabel : (route.variantLabel ?? meta.label)} />
              ) : null}
              {/* Always-on absolute highway % chip (expanded view). Matches
               *  the same chip in the collapsed view so the data point is
               *  consistent across selected and non-selected card states.
               *  Real-world feedback: "the route cards should all have
               *  available info to compare." */}
              {Number.isFinite(thisHighwayPct ?? NaN) && (thisHighwayPct as number) >= 10 ? (
                <span
                  className={`text-[11px] px-2.5 py-0.5 rounded-full tabular-nums font-medium ${
                    (thisHighwayPct as number) >= 60
                      ? "bg-amber-500/15 text-amber-700 dark:text-amber-400"
                      : "bg-muted text-foreground/70"
                  }`}
                  title={`${Math.round(thisHighwayPct as number)}% of this route runs on highway`}
                >
                  {Math.round(thisHighwayPct as number)}% highway
                </span>
              ) : null}
              {!useGoogleBaseline && googleDurationS != null ? (
                <GoogleEtaBadge thisDurationS={route.route.duration} googleDurationS={googleDurationS} thisDistanceM={route.route.distance} googleDistanceM={googleDistanceM} size="sm" />
              ) : null}
              {/* Subtle "includes traffic" tag — shown when the displayed
               *  minutes already include live-traffic delay (>60s of it).
               *  The previous "+X min traffic" pill was confusing because
               *  users saw "10 min" + "+7 min traffic" and didn't know
               *  which number to trust. Now the time IS the truth and the
               *  tag just clarifies "this number reflects traffic." */}
              {/* "incl. ~X min traffic" tag removed with the item-2 ETA
                *  policy (Aug 11): the displayed minutes are route.duration
                *  (already traffic-aware via the driving-traffic profile);
                *  the secondary estimate no longer feeds the displayed
                *  number, so a tag claiming it does would lie. */}
              {extraMin > 0 && !useGoogleBaseline ? (
                <EtaConfidenceChip extraMin={extraMin} fastestDurationS={fastestDuration} size="sm" />
              ) : null}
              {extraMin > 0 && baselineMode && baselineOptions && onBaselineModeChange ? (
                <BaselineToggle mode={baselineMode} options={baselineOptions} onChange={onBaselineModeChange} size="sm" />
              ) : null}
            </div>
          </div>

          {/* Brand subtitle ("A longer loop through the good stuff", "Small
           *  detours through nicer streets", etc.) — pretty but it isn't
           *  comparable information across cards and was adding a line of
           *  height to selected cards only, making the size delta vs the
           *  collapsed cards feel jarring. Keep the data, drop the prose:
           *  the topFactors list below is the substantive "why this route"
           *  signal.  Real-world feedback: "the cards are still too long." */}

          {/* SameAsFastestPanel removed per real-world feedback — too wordy
           *  and got in the way of the at-a-glance card reading. The pleasant
           *  "= fastest" case still has the inline `Fastest & Wander` badge
           *  in the title row, which is enough signal. */}

          {/* THROUGH-A-PARK CELEBRATION (Morgan: "any time the walking route
           *  goes THROUGH a state park, say so"). `fastThroughPark` is only ever
           *  set for a walk/bike route VERIFIABLY running through a park, so this
           *  shows on ANY such card regardless of classification — except when
           *  the only-option sub below already names the park, to avoid repeating
           *  it. */}
          {fastThroughPark && !(isOnlyOption && fastIsPleasant) && (
            <p className="mt-1 text-[12px] leading-snug font-medium text-pleasant flex items-start gap-1">
              <Leaf className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden />
              <span>
                {_words.goesThrough} {fastThroughPark}
                {fastOnTrails ? " on car-free trails" : ""} — a path the usual apps won&apos;t route you on.
              </span>
            </p>
          )}

          {/* ONLY-OPTION SUB: when the engine has nothing meaningfully
           *  different from Google to offer, frame the single card as the
           *  direct way, not a pleasant alternative. */}
          {isOnlyOption && (
            <p className="mt-1 text-[12px] leading-snug text-foreground/75">
              {fastIsPleasant
                ? `Fast and pleasant — ${fastPleasantDetail}${fastThroughPark ? ", a path the usual apps won't route you on." : "."}`
                : "The most direct way here."}
            </p>
          )}
          {/* NO MIDDLE OPTION (Morgan, Aug 9). One sentence, no explanation
           *  after it — her wording exactly. Set only when a Scenic card is
           *  present and the Pleasant slot came up empty, so it reads as
           *  "there's no in-between here", not "there's nothing here". */}
          {/* Don't say "no reasonable middle option" on a card already titled
              "Fast & pleasant" — that reads as an apology for the good news.
              The line is for the case where the fast route ISN'T pleasant and
              nothing calmer exists. */}
          {noMiddleOption && !isOnlyOption && !fastIsPleasant && (
            <p className="mt-1 text-[12px] leading-snug text-foreground/75">
              No reasonable middle option on this drive
            </p>
          )}


          {/* FERRIES ARE DISCLOSED, NEVER HIDDEN (Morgan, Aug 9: "if there is
            * one it needs to be mentioned in the title card and in the
            * directions"; "ferries are ok with me").
            *
            * Umbria → Nice offered a 15h 16m "calm" route that was a drive to
            * a port, a boat to Corsica and a second boat to Nice. Nothing said
            * so. It read as an ordinary back-road drive because sea isn't
            * tagged motorway — the route scored hwy7% and won the calm filter
            * on the strength of it.
            *
            * The data was always here: RouteStep.classes carries Mapbox's
            * intersection classes and "ferry" is one of them (routing.ts:21).
            * Nobody had ever read it. A driver may absolutely want the boat —
            * but never by accident, and never without the hours attached. */}
          {(() => {
            const steps = route.route.steps ?? [];
            // Count CROSSINGS, not steps: one sailing can span several steps,
            // so only a non-ferry → ferry transition starts a new crossing.
            let crossings = 0;
            let ferrySec = 0;
            let prevWasFerry = false;
            for (const s of steps) {
              const isFerry = (s.classes ?? []).includes("ferry");
              if (isFerry) {
                if (!prevWasFerry) crossings++;
                ferrySec += s.duration ?? 0;
              }
              prevWasFerry = isFerry;
            }
            if (crossings === 0) return null;
            const h = Math.floor(ferrySec / 3600);
            const m = Math.round((ferrySec % 3600) / 60);
            const onboard = h > 0 ? `${h}h ${m}m` : `${m}m`;
            return (
              <p className="mt-1 text-[12px] leading-snug text-foreground/75">
                {crossings === 1
                  ? `Includes a ferry crossing — ${onboard} on board`
                  : `Includes ${crossings} ferry crossings — ${onboard} on board`}
              </p>
            );
          })()}

          {/* PLEASANT-WIN HEADLINE: when the pleasant card is essentially
           *  the same time as Google (±2 min) AND uses meaningfully less
           *  highway, surface that out loud. "Same time as Google — a
           *  nicer way" is the headline framing for the trips where the
           *  user gets the pleasant route at zero time cost. */}
          {!isOnlyOption && isPleasant && !isFakeWander && (() => {
            if (googleDurationS == null || !Number.isFinite(googleDurationS)) return null;
            const deltaSec = route.route.duration - googleDurationS;
            const within2Min = Math.abs(deltaSec) <= 120;
            const hwyThis = Number.isFinite(thisHighwayPct ?? NaN) ? (thisHighwayPct as number) : null;
            const hwyGoogle = Number.isFinite(googleHighwayPct ?? NaN) ? (googleHighwayPct as number) : null;
            const lessHighway =
              hwyThis != null && hwyGoogle != null && (hwyGoogle - hwyThis) >= 10;
            if (!within2Min || !lessHighway) return null;
            return (
              <p className="mt-1 text-[12px] leading-snug font-medium text-primary">
                <Sparkles className="inline w-3 h-3 mr-1 -mt-0.5" aria-hidden />
                Same time as the fast route — a nicer way.
              </p>
            );
          })()}

          {/* (#193) "Try Scenic" CTA removed. Under three-cards-always
           *  (#190) the user picks by tapping a card, not by silently
           *  rewriting their tolerance preset. The Adventure card is
           *  always offered when a distinct scenic alternative exists,
           *  so highway-heavy Pleasant picks no longer need a separate
           *  escape hatch — Adventure is right there. */}

          {useGoogleBaseline && (
            <p
              className="mt-1 text-[12px] leading-snug text-foreground/75"
              aria-label="Why this route"
            >
              <span className="font-medium text-foreground/85">Why this route:</span>{" "}
              {Number.isFinite(googleHighwayPct ?? NaN)
                ? `Prioritizes a direct path on major roads (${Math.round(googleHighwayPct as number)}% main roads) — fewer parks, water, and quieter streets than the Pleasant pick.`
                : "Prioritizes a direct path on major roads — fewer parks, water, and quieter streets than the Pleasant pick."}
            </p>
          )}

          {/* Factor chips, parked by Morgan in 663309d8 (Jun 10 2026,
           *  "Redesign route cards: one-sentence why + clean details") — the
           *  one-sentence "Why this route" above replaced this list. Kept
           *  behind the switch rather than deleted. */}
          {/* eslint-disable-next-line no-constant-binary-expression */}
          {false && topFactors.length > 0 && (
            <ul
              className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] leading-snug"
              aria-label="Why this route"
            >
              {topFactors.map((fac, i) => {
                const dotCls =
                  fac.kind === "positive"
                    ? "bg-emerald-500"
                    : fac.kind === "caveat"
                    ? "bg-amber-500"
                    : "bg-muted-foreground/60";
                const textCls =
                  fac.kind === "caveat" ? "text-foreground/70" : "text-foreground/85";
                const confLabel =
                  fac.confidence === "high"
                    ? "high confidence"
                    : fac.confidence === "medium"
                    ? "medium confidence"
                    : "low confidence";
                const confOpacity =
                  fac.confidence === "high"
                    ? "opacity-100"
                    : fac.confidence === "medium"
                    ? "opacity-70"
                    : "opacity-40";
                const highlight = factorHighlights[i];
                const isClickable = !!highlight;
                const inner = (
                  <>
                    <span
                      className={`inline-block w-1.5 h-1.5 rounded-full ${dotCls} ${confOpacity}`}
                      aria-hidden="true"
                      title={confLabel}
                    />
                    <span className={textCls}>{fac.text}</span>
                    {isClickable ? (
                      <MapPin
                        className="w-3 h-3 text-muted-foreground/70 group-hover:text-primary transition-colors"
                        aria-hidden="true"
                      />
                    ) : null}
                    <span className="sr-only"> ({confLabel})</span>
                  </>
                );
                return (
                  <li key={i} className="flex items-center">
                    {isClickable ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          logRouteInteraction({
                            action: "why_click",
                            topic: fac.topic ?? null,
                            factorText: fac.text,
                            routeIndex: typeof optionNumber === "number" ? optionNumber - 1 : null,
                            routePleasantScore: route.scores?.pleasant ?? null,
                            routeDistanceM: route.route?.distance ?? null,
                            routeDurationS: route.route?.duration ?? null,
                            isRecommended: !!recommended,
                          });
                          requestMapHighlight(highlight!);
                        }}
                        className="group inline-flex items-center gap-1.5 rounded-md px-1 -mx-1 py-0.5 hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 transition-colors cursor-pointer"
                        aria-label={`Show "${fac.text}" on the map`}
                        title="Show on map"
                      >
                        {inner}
                      </button>
                    ) : (
                      <span className="inline-flex items-center gap-1.5">{inner}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}


          {/* Time + distance duplicate row removed (#115b) — the
              compact header above already shows fmtMin / fmtKm next
              to the +X min pill, so re-rendering them here was the
              same info twice. The "coast time" / "water stretch"
              factoid below still lives in its own row. */}
          <div className="flex items-center gap-4 text-sm text-muted-foreground mt-2.5">
            {(() => {
              // "Coast time" / "Water stretch" metric — mirrors the lead
              // why-this-route line. Only shown when the route actually
              // runs along water for a meaningful stretch (≥ ~250 m), so
              // it stays in sync with the first explanation chip and
              // doesn't add noise to inland routes.
              const isOcean = coastM >= 400;
              const distM = isOcean ? coastM : waterAdjMeters;
              if (!distM || distM < 250) return null;
              const totalM = route.route.distance;
              const pct = totalM > 0 ? Math.round((distM / totalM) * 100) : 0;
              if (pct < 1) return null;
              const distStr = fmtAdjMi(distM / 1609.34);
              const Icon = isOcean ? Waves : Droplets;
              const label = isOcean ? "Coast time" : "Water stretch";
              const colorCls = isOcean
                ? "text-sky-600 dark:text-sky-400"
                : "text-cyan-600 dark:text-cyan-400";
              return (
                <span
                  className={`flex items-center gap-1.5 ${colorCls}`}
                  // Aug 19 units audit: fmtAdjMi now returns value + unit in
                  // the driver's chosen system, so no hardcoded mi/miles here.
                  title={`${label}: ${distStr} (${pct}% of route) ${isOcean ? "along the coast" : "along water"}`}
                  aria-label={`${label}: ${distStr}, ${pct}% of route`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="tabular-nums">
                    {distStr}
                    <span className="text-muted-foreground/80"> · {pct}%</span>
                  </span>
                </span>
              );
            })()}
          </div>

          {/* Pleasant-score bar removed — wasn't conveying useful signal.
           *  Route quality is now communicated via the evidence chips below
           *  and the variant label above. */}

          {/* Evidence chips. Dedupe against the "why this route" factors
           *  rendered above — repeating "6.2 mi along Pacific Coast Highway"
           *  as both a factor line AND a chip just makes the card busier
           *  without adding information. */}
          {(() => {
            const norm = (s: string) =>
              s.toLowerCase().replace(/\([^)]*\)/g, "").replace(/[^a-z0-9]+/g, " ").trim();
            const factorKeys = topFactors.map((tf) => norm(tf.text)).filter(Boolean);
            const dedupedChips = chips.filter((c) => {
              const k = norm(c);
              if (!k) return true;
              return !factorKeys.some((fk) => fk.includes(k) || k.includes(fk));
            });
            return dedupedChips.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {dedupedChips.map((c, i) => {
                const isTimeAware = TIME_AWARE_RE.test(c);
                const bad = isNegativeChip(c);
                if (isTimeAware) {
                  return (
                    <TooltipProvider key={i} delayDuration={150}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            className={`text-[11px] px-2 py-0.5 rounded-full inline-flex items-center gap-1 cursor-help border border-dashed ${
                              bad
                                ? "bg-destructive/5 text-destructive border-destructive/30"
                                : "bg-primary/5 text-primary border-primary/30"
                            }`}
                          >
                            <Clock3 className="w-3 h-3" />
                            {c}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[260px] text-xs leading-snug">
                          <div className="font-semibold mb-1 flex items-center gap-1.5">
                            <Clock3 className="w-3 h-3" />
                            Active window: {TIME_WINDOW_LABEL[getTimeWindow()]}
                          </div>
                          Time-aware adjustment based on typical patterns for this time of day (rush hour, weekend daytime, late night, etc.). Not live traffic — just a small nudge to the route's pleasantness score.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                }
                return (
                  <span
                    key={i}
                    className={`text-[11px] px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${
                      bad
                        ? "bg-destructive/10 text-destructive"
                        : "bg-muted text-foreground/80"
                    }`}
                  >
                    {bad && <AlertTriangle className="w-3 h-3" />}
                    {c}
                  </span>
                );
              })}
            </div>
            );
          })()}

          {/* "See details" breakdown removed (Morgan: the raw details panel
           *  doesn't help — the card face already names what's on the route). */}

          {/* "Why this route?" affordance. Hidden on a PLAIN fastest card (a
           *  pure quickest-path needs no justification), but SHOWN when the
           *  fastest way is also the pleasant one — there the roads it goes down
           *  and why it's nice are exactly what the user wants (Morgan). */}
          {selected && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {onToggleWhy &&
                (route.classification !== "fastest" || fastIsPleasant) && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onToggleWhy(); }}
                  aria-expanded={whyOpen}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary min-h-11 px-3 py-2 rounded-full border border-primary/30 bg-primary/5 hover:bg-primary/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background touch-manipulation"
                >
                  <Sparkles className="w-4 h-4" />
                  Why this route?
                </button>
              )}
              {onOpenDetails && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onOpenDetails(); }}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground min-h-11 px-3 py-2 rounded-full border border-border bg-card hover:bg-muted/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background touch-manipulation"
                >
                  <Info className="w-4 h-4" />
                  Trip details
                </button>
              )}
              {/* "Detours" chip REMOVED pre-2.3 (Morgan, Aug 8: "take off
                * detours for now, they don't work — keep add a stop"). The
                * Add-a-stop section below the card carries the stop flow;
                * onOpenDetours stays wired for when the panel earns its
                * way back. */}
            </div>
          )}
          {/* Offered detours listed inline on the selected card (Morgan). */}
          {selected && detourPanel && (
            <div className="mt-3" onClick={(e) => e.stopPropagation()}>
              {detourPanel}
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
};
